import guitar, { makeRule, block } from 'guitar';
import get from 'lodash/get';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import * as React from 'react';
import React__default, { createContext, useRef, useCallback, useState, useLayoutEffect, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Look up a property from a theme
function themeLookup(theme, key) {
    return get(theme, `RoadieUI.${key}`);
}

const checkTheme = (str) => {
    return (props) => {
        return themeLookup(props.theme, str) || str;
    };
};

const em = (n) => `${n / 16}em`;
const rule = makeRule({
    defaultParser: checkTheme,
    media: {
        desktop: `(min-width: ${em(901)})`,
        tablet: `(min-width: ${em(601)}) AND (max-width: ${em(900)})`,
        mobile: `(max-width: ${em(600)})`,
    },
});

function _extends$c() {
  _extends$c = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$c.apply(this, arguments);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _isNativeFunction(fn) {
  return Function.toString.call(fn).indexOf("[native code]") !== -1;
}

function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _construct(Parent, args, Class) {
  if (_isNativeReflectConstruct()) {
    _construct = Reflect.construct;
  } else {
    _construct = function _construct(Parent, args, Class) {
      var a = [null];
      a.push.apply(a, args);
      var Constructor = Function.bind.apply(Parent, a);
      var instance = new Constructor();
      if (Class) _setPrototypeOf(instance, Class.prototype);
      return instance;
    };
  }

  return _construct.apply(null, arguments);
}

function _wrapNativeSuper(Class) {
  var _cache = typeof Map === "function" ? new Map() : undefined;

  _wrapNativeSuper = function _wrapNativeSuper(Class) {
    if (Class === null || !_isNativeFunction(Class)) return Class;

    if (typeof Class !== "function") {
      throw new TypeError("Super expression must either be null or a function");
    }

    if (typeof _cache !== "undefined") {
      if (_cache.has(Class)) return _cache.get(Class);

      _cache.set(Class, Wrapper);
    }

    function Wrapper() {
      return _construct(Class, arguments, _getPrototypeOf(this).constructor);
    }

    Wrapper.prototype = Object.create(Class.prototype, {
      constructor: {
        value: Wrapper,
        enumerable: false,
        writable: true,
        configurable: true
      }
    });
    return _setPrototypeOf(Wrapper, Class);
  };

  return _wrapNativeSuper(Class);
}

/**
 * Create an error file out of errors.md for development and a simple web link to the full errors
 * in production mode.
 * @private
 */


var PolishedError = /*#__PURE__*/function (_Error) {
  _inheritsLoose(PolishedError, _Error);

  function PolishedError(code) {
    var _this;

    {
      _this = _Error.call(this, "An error occurred. See https://github.com/styled-components/polished/blob/main/src/internalHelpers/errors.md#" + code + " for more information.") || this;
    }

    return _assertThisInitialized(_this);
  }

  return PolishedError;
}( /*#__PURE__*/_wrapNativeSuper(Error));

function colorToInt(color) {
  return Math.round(color * 255);
}

function convertToInt(red, green, blue) {
  return colorToInt(red) + "," + colorToInt(green) + "," + colorToInt(blue);
}

function hslToRgb(hue, saturation, lightness, convert) {
  if (convert === void 0) {
    convert = convertToInt;
  }

  if (saturation === 0) {
    // achromatic
    return convert(lightness, lightness, lightness);
  } // formulae from https://en.wikipedia.org/wiki/HSL_and_HSV


  var huePrime = (hue % 360 + 360) % 360 / 60;
  var chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  var secondComponent = chroma * (1 - Math.abs(huePrime % 2 - 1));
  var red = 0;
  var green = 0;
  var blue = 0;

  if (huePrime >= 0 && huePrime < 1) {
    red = chroma;
    green = secondComponent;
  } else if (huePrime >= 1 && huePrime < 2) {
    red = secondComponent;
    green = chroma;
  } else if (huePrime >= 2 && huePrime < 3) {
    green = chroma;
    blue = secondComponent;
  } else if (huePrime >= 3 && huePrime < 4) {
    green = secondComponent;
    blue = chroma;
  } else if (huePrime >= 4 && huePrime < 5) {
    red = secondComponent;
    blue = chroma;
  } else if (huePrime >= 5 && huePrime < 6) {
    red = chroma;
    blue = secondComponent;
  }

  var lightnessModification = lightness - chroma / 2;
  var finalRed = red + lightnessModification;
  var finalGreen = green + lightnessModification;
  var finalBlue = blue + lightnessModification;
  return convert(finalRed, finalGreen, finalBlue);
}

var namedColorMap = {
  aliceblue: 'f0f8ff',
  antiquewhite: 'faebd7',
  aqua: '00ffff',
  aquamarine: '7fffd4',
  azure: 'f0ffff',
  beige: 'f5f5dc',
  bisque: 'ffe4c4',
  black: '000',
  blanchedalmond: 'ffebcd',
  blue: '0000ff',
  blueviolet: '8a2be2',
  brown: 'a52a2a',
  burlywood: 'deb887',
  cadetblue: '5f9ea0',
  chartreuse: '7fff00',
  chocolate: 'd2691e',
  coral: 'ff7f50',
  cornflowerblue: '6495ed',
  cornsilk: 'fff8dc',
  crimson: 'dc143c',
  cyan: '00ffff',
  darkblue: '00008b',
  darkcyan: '008b8b',
  darkgoldenrod: 'b8860b',
  darkgray: 'a9a9a9',
  darkgreen: '006400',
  darkgrey: 'a9a9a9',
  darkkhaki: 'bdb76b',
  darkmagenta: '8b008b',
  darkolivegreen: '556b2f',
  darkorange: 'ff8c00',
  darkorchid: '9932cc',
  darkred: '8b0000',
  darksalmon: 'e9967a',
  darkseagreen: '8fbc8f',
  darkslateblue: '483d8b',
  darkslategray: '2f4f4f',
  darkslategrey: '2f4f4f',
  darkturquoise: '00ced1',
  darkviolet: '9400d3',
  deeppink: 'ff1493',
  deepskyblue: '00bfff',
  dimgray: '696969',
  dimgrey: '696969',
  dodgerblue: '1e90ff',
  firebrick: 'b22222',
  floralwhite: 'fffaf0',
  forestgreen: '228b22',
  fuchsia: 'ff00ff',
  gainsboro: 'dcdcdc',
  ghostwhite: 'f8f8ff',
  gold: 'ffd700',
  goldenrod: 'daa520',
  gray: '808080',
  green: '008000',
  greenyellow: 'adff2f',
  grey: '808080',
  honeydew: 'f0fff0',
  hotpink: 'ff69b4',
  indianred: 'cd5c5c',
  indigo: '4b0082',
  ivory: 'fffff0',
  khaki: 'f0e68c',
  lavender: 'e6e6fa',
  lavenderblush: 'fff0f5',
  lawngreen: '7cfc00',
  lemonchiffon: 'fffacd',
  lightblue: 'add8e6',
  lightcoral: 'f08080',
  lightcyan: 'e0ffff',
  lightgoldenrodyellow: 'fafad2',
  lightgray: 'd3d3d3',
  lightgreen: '90ee90',
  lightgrey: 'd3d3d3',
  lightpink: 'ffb6c1',
  lightsalmon: 'ffa07a',
  lightseagreen: '20b2aa',
  lightskyblue: '87cefa',
  lightslategray: '789',
  lightslategrey: '789',
  lightsteelblue: 'b0c4de',
  lightyellow: 'ffffe0',
  lime: '0f0',
  limegreen: '32cd32',
  linen: 'faf0e6',
  magenta: 'f0f',
  maroon: '800000',
  mediumaquamarine: '66cdaa',
  mediumblue: '0000cd',
  mediumorchid: 'ba55d3',
  mediumpurple: '9370db',
  mediumseagreen: '3cb371',
  mediumslateblue: '7b68ee',
  mediumspringgreen: '00fa9a',
  mediumturquoise: '48d1cc',
  mediumvioletred: 'c71585',
  midnightblue: '191970',
  mintcream: 'f5fffa',
  mistyrose: 'ffe4e1',
  moccasin: 'ffe4b5',
  navajowhite: 'ffdead',
  navy: '000080',
  oldlace: 'fdf5e6',
  olive: '808000',
  olivedrab: '6b8e23',
  orange: 'ffa500',
  orangered: 'ff4500',
  orchid: 'da70d6',
  palegoldenrod: 'eee8aa',
  palegreen: '98fb98',
  paleturquoise: 'afeeee',
  palevioletred: 'db7093',
  papayawhip: 'ffefd5',
  peachpuff: 'ffdab9',
  peru: 'cd853f',
  pink: 'ffc0cb',
  plum: 'dda0dd',
  powderblue: 'b0e0e6',
  purple: '800080',
  rebeccapurple: '639',
  red: 'f00',
  rosybrown: 'bc8f8f',
  royalblue: '4169e1',
  saddlebrown: '8b4513',
  salmon: 'fa8072',
  sandybrown: 'f4a460',
  seagreen: '2e8b57',
  seashell: 'fff5ee',
  sienna: 'a0522d',
  silver: 'c0c0c0',
  skyblue: '87ceeb',
  slateblue: '6a5acd',
  slategray: '708090',
  slategrey: '708090',
  snow: 'fffafa',
  springgreen: '00ff7f',
  steelblue: '4682b4',
  tan: 'd2b48c',
  teal: '008080',
  thistle: 'd8bfd8',
  tomato: 'ff6347',
  turquoise: '40e0d0',
  violet: 'ee82ee',
  wheat: 'f5deb3',
  white: 'fff',
  whitesmoke: 'f5f5f5',
  yellow: 'ff0',
  yellowgreen: '9acd32'
};
/**
 * Checks if a string is a CSS named color and returns its equivalent hex value, otherwise returns the original color.
 * @private
 */

function nameToHex(color) {
  if (typeof color !== 'string') return color;
  var normalizedColorName = color.toLowerCase();
  return namedColorMap[normalizedColorName] ? "#" + namedColorMap[normalizedColorName] : color;
}

var hexRegex = /^#[a-fA-F0-9]{6}$/;
var hexRgbaRegex = /^#[a-fA-F0-9]{8}$/;
var reducedHexRegex = /^#[a-fA-F0-9]{3}$/;
var reducedRgbaHexRegex = /^#[a-fA-F0-9]{4}$/;
var rgbRegex = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i;
var rgbaRegex = /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*([-+]?[0-9]*[.]?[0-9]+)\s*\)$/i;
var hslRegex = /^hsl\(\s*(\d{0,3}[.]?[0-9]+)\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*\)$/i;
var hslaRegex = /^hsla\(\s*(\d{0,3}[.]?[0-9]+)\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*,\s*([-+]?[0-9]*[.]?[0-9]+)\s*\)$/i;
/**
 * Returns an RgbColor or RgbaColor object. This utility function is only useful
 * if want to extract a color component. With the color util `toColorString` you
 * can convert a RgbColor or RgbaColor object back to a string.
 *
 * @example
 * // Assigns `{ red: 255, green: 0, blue: 0 }` to color1
 * const color1 = parseToRgb('rgb(255, 0, 0)');
 * // Assigns `{ red: 92, green: 102, blue: 112, alpha: 0.75 }` to color2
 * const color2 = parseToRgb('hsla(210, 10%, 40%, 0.75)');
 */

function parseToRgb(color) {
  if (typeof color !== 'string') {
    throw new PolishedError(3);
  }

  var normalizedColor = nameToHex(color);

  if (normalizedColor.match(hexRegex)) {
    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[2], 16),
      green: parseInt("" + normalizedColor[3] + normalizedColor[4], 16),
      blue: parseInt("" + normalizedColor[5] + normalizedColor[6], 16)
    };
  }

  if (normalizedColor.match(hexRgbaRegex)) {
    var alpha = parseFloat((parseInt("" + normalizedColor[7] + normalizedColor[8], 16) / 255).toFixed(2));
    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[2], 16),
      green: parseInt("" + normalizedColor[3] + normalizedColor[4], 16),
      blue: parseInt("" + normalizedColor[5] + normalizedColor[6], 16),
      alpha: alpha
    };
  }

  if (normalizedColor.match(reducedHexRegex)) {
    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[1], 16),
      green: parseInt("" + normalizedColor[2] + normalizedColor[2], 16),
      blue: parseInt("" + normalizedColor[3] + normalizedColor[3], 16)
    };
  }

  if (normalizedColor.match(reducedRgbaHexRegex)) {
    var _alpha = parseFloat((parseInt("" + normalizedColor[4] + normalizedColor[4], 16) / 255).toFixed(2));

    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[1], 16),
      green: parseInt("" + normalizedColor[2] + normalizedColor[2], 16),
      blue: parseInt("" + normalizedColor[3] + normalizedColor[3], 16),
      alpha: _alpha
    };
  }

  var rgbMatched = rgbRegex.exec(normalizedColor);

  if (rgbMatched) {
    return {
      red: parseInt("" + rgbMatched[1], 10),
      green: parseInt("" + rgbMatched[2], 10),
      blue: parseInt("" + rgbMatched[3], 10)
    };
  }

  var rgbaMatched = rgbaRegex.exec(normalizedColor.substring(0, 50));

  if (rgbaMatched) {
    return {
      red: parseInt("" + rgbaMatched[1], 10),
      green: parseInt("" + rgbaMatched[2], 10),
      blue: parseInt("" + rgbaMatched[3], 10),
      alpha: parseFloat("" + rgbaMatched[4])
    };
  }

  var hslMatched = hslRegex.exec(normalizedColor);

  if (hslMatched) {
    var hue = parseInt("" + hslMatched[1], 10);
    var saturation = parseInt("" + hslMatched[2], 10) / 100;
    var lightness = parseInt("" + hslMatched[3], 10) / 100;
    var rgbColorString = "rgb(" + hslToRgb(hue, saturation, lightness) + ")";
    var hslRgbMatched = rgbRegex.exec(rgbColorString);

    if (!hslRgbMatched) {
      throw new PolishedError(4, normalizedColor, rgbColorString);
    }

    return {
      red: parseInt("" + hslRgbMatched[1], 10),
      green: parseInt("" + hslRgbMatched[2], 10),
      blue: parseInt("" + hslRgbMatched[3], 10)
    };
  }

  var hslaMatched = hslaRegex.exec(normalizedColor.substring(0, 50));

  if (hslaMatched) {
    var _hue = parseInt("" + hslaMatched[1], 10);

    var _saturation = parseInt("" + hslaMatched[2], 10) / 100;

    var _lightness = parseInt("" + hslaMatched[3], 10) / 100;

    var _rgbColorString = "rgb(" + hslToRgb(_hue, _saturation, _lightness) + ")";

    var _hslRgbMatched = rgbRegex.exec(_rgbColorString);

    if (!_hslRgbMatched) {
      throw new PolishedError(4, normalizedColor, _rgbColorString);
    }

    return {
      red: parseInt("" + _hslRgbMatched[1], 10),
      green: parseInt("" + _hslRgbMatched[2], 10),
      blue: parseInt("" + _hslRgbMatched[3], 10),
      alpha: parseFloat("" + hslaMatched[4])
    };
  }

  throw new PolishedError(5);
}

function rgbToHsl(color) {
  // make sure rgb are contained in a set of [0, 255]
  var red = color.red / 255;
  var green = color.green / 255;
  var blue = color.blue / 255;
  var max = Math.max(red, green, blue);
  var min = Math.min(red, green, blue);
  var lightness = (max + min) / 2;

  if (max === min) {
    // achromatic
    if (color.alpha !== undefined) {
      return {
        hue: 0,
        saturation: 0,
        lightness: lightness,
        alpha: color.alpha
      };
    } else {
      return {
        hue: 0,
        saturation: 0,
        lightness: lightness
      };
    }
  }

  var hue;
  var delta = max - min;
  var saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);

  switch (max) {
    case red:
      hue = (green - blue) / delta + (green < blue ? 6 : 0);
      break;

    case green:
      hue = (blue - red) / delta + 2;
      break;

    default:
      // blue case
      hue = (red - green) / delta + 4;
      break;
  }

  hue *= 60;

  if (color.alpha !== undefined) {
    return {
      hue: hue,
      saturation: saturation,
      lightness: lightness,
      alpha: color.alpha
    };
  }

  return {
    hue: hue,
    saturation: saturation,
    lightness: lightness
  };
}

/**
 * Returns an HslColor or HslaColor object. This utility function is only useful
 * if want to extract a color component. With the color util `toColorString` you
 * can convert a HslColor or HslaColor object back to a string.
 *
 * @example
 * // Assigns `{ hue: 0, saturation: 1, lightness: 0.5 }` to color1
 * const color1 = parseToHsl('rgb(255, 0, 0)');
 * // Assigns `{ hue: 128, saturation: 1, lightness: 0.5, alpha: 0.75 }` to color2
 * const color2 = parseToHsl('hsla(128, 100%, 50%, 0.75)');
 */
function parseToHsl(color) {
  // Note: At a later stage we can optimize this function as right now a hsl
  // color would be parsed converted to rgb values and converted back to hsl.
  return rgbToHsl(parseToRgb(color));
}

/**
 * Reduces hex values if possible e.g. #ff8866 to #f86
 * @private
 */
var reduceHexValue = function reduceHexValue(value) {
  if (value.length === 7 && value[1] === value[2] && value[3] === value[4] && value[5] === value[6]) {
    return "#" + value[1] + value[3] + value[5];
  }

  return value;
};

var reduceHexValue$1 = reduceHexValue;

function numberToHex(value) {
  var hex = value.toString(16);
  return hex.length === 1 ? "0" + hex : hex;
}

function colorToHex(color) {
  return numberToHex(Math.round(color * 255));
}

function convertToHex(red, green, blue) {
  return reduceHexValue$1("#" + colorToHex(red) + colorToHex(green) + colorToHex(blue));
}

function hslToHex(hue, saturation, lightness) {
  return hslToRgb(hue, saturation, lightness, convertToHex);
}

/**
 * Returns a string value for the color. The returned result is the smallest possible hex notation.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: hsl(359, 0.75, 0.4),
 *   background: hsl({ hue: 360, saturation: 0.75, lightness: 0.4 }),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${hsl(359, 0.75, 0.4)};
 *   background: ${hsl({ hue: 360, saturation: 0.75, lightness: 0.4 })};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "#b3191c";
 *   background: "#b3191c";
 * }
 */
function hsl(value, saturation, lightness) {
  if (typeof value === 'number' && typeof saturation === 'number' && typeof lightness === 'number') {
    return hslToHex(value, saturation, lightness);
  } else if (typeof value === 'object' && saturation === undefined && lightness === undefined) {
    return hslToHex(value.hue, value.saturation, value.lightness);
  }

  throw new PolishedError(1);
}

/**
 * Returns a string value for the color. The returned result is the smallest possible rgba or hex notation.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: hsla(359, 0.75, 0.4, 0.7),
 *   background: hsla({ hue: 360, saturation: 0.75, lightness: 0.4, alpha: 0,7 }),
 *   background: hsla(359, 0.75, 0.4, 1),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${hsla(359, 0.75, 0.4, 0.7)};
 *   background: ${hsla({ hue: 360, saturation: 0.75, lightness: 0.4, alpha: 0,7 })};
 *   background: ${hsla(359, 0.75, 0.4, 1)};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "rgba(179,25,28,0.7)";
 *   background: "rgba(179,25,28,0.7)";
 *   background: "#b3191c";
 * }
 */
function hsla(value, saturation, lightness, alpha) {
  if (typeof value === 'number' && typeof saturation === 'number' && typeof lightness === 'number' && typeof alpha === 'number') {
    return alpha >= 1 ? hslToHex(value, saturation, lightness) : "rgba(" + hslToRgb(value, saturation, lightness) + "," + alpha + ")";
  } else if (typeof value === 'object' && saturation === undefined && lightness === undefined && alpha === undefined) {
    return value.alpha >= 1 ? hslToHex(value.hue, value.saturation, value.lightness) : "rgba(" + hslToRgb(value.hue, value.saturation, value.lightness) + "," + value.alpha + ")";
  }

  throw new PolishedError(2);
}

/**
 * Returns a string value for the color. The returned result is the smallest possible hex notation.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: rgb(255, 205, 100),
 *   background: rgb({ red: 255, green: 205, blue: 100 }),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${rgb(255, 205, 100)};
 *   background: ${rgb({ red: 255, green: 205, blue: 100 })};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "#ffcd64";
 *   background: "#ffcd64";
 * }
 */
function rgb(value, green, blue) {
  if (typeof value === 'number' && typeof green === 'number' && typeof blue === 'number') {
    return reduceHexValue$1("#" + numberToHex(value) + numberToHex(green) + numberToHex(blue));
  } else if (typeof value === 'object' && green === undefined && blue === undefined) {
    return reduceHexValue$1("#" + numberToHex(value.red) + numberToHex(value.green) + numberToHex(value.blue));
  }

  throw new PolishedError(6);
}

/**
 * Returns a string value for the color. The returned result is the smallest possible rgba or hex notation.
 *
 * Can also be used to fade a color by passing a hex value or named CSS color along with an alpha value.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: rgba(255, 205, 100, 0.7),
 *   background: rgba({ red: 255, green: 205, blue: 100, alpha: 0.7 }),
 *   background: rgba(255, 205, 100, 1),
 *   background: rgba('#ffffff', 0.4),
 *   background: rgba('black', 0.7),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${rgba(255, 205, 100, 0.7)};
 *   background: ${rgba({ red: 255, green: 205, blue: 100, alpha: 0.7 })};
 *   background: ${rgba(255, 205, 100, 1)};
 *   background: ${rgba('#ffffff', 0.4)};
 *   background: ${rgba('black', 0.7)};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "rgba(255,205,100,0.7)";
 *   background: "rgba(255,205,100,0.7)";
 *   background: "#ffcd64";
 *   background: "rgba(255,255,255,0.4)";
 *   background: "rgba(0,0,0,0.7)";
 * }
 */
function rgba(firstValue, secondValue, thirdValue, fourthValue) {
  if (typeof firstValue === 'string' && typeof secondValue === 'number') {
    var rgbValue = parseToRgb(firstValue);
    return "rgba(" + rgbValue.red + "," + rgbValue.green + "," + rgbValue.blue + "," + secondValue + ")";
  } else if (typeof firstValue === 'number' && typeof secondValue === 'number' && typeof thirdValue === 'number' && typeof fourthValue === 'number') {
    return fourthValue >= 1 ? rgb(firstValue, secondValue, thirdValue) : "rgba(" + firstValue + "," + secondValue + "," + thirdValue + "," + fourthValue + ")";
  } else if (typeof firstValue === 'object' && secondValue === undefined && thirdValue === undefined && fourthValue === undefined) {
    return firstValue.alpha >= 1 ? rgb(firstValue.red, firstValue.green, firstValue.blue) : "rgba(" + firstValue.red + "," + firstValue.green + "," + firstValue.blue + "," + firstValue.alpha + ")";
  }

  throw new PolishedError(7);
}

var isRgb = function isRgb(color) {
  return typeof color.red === 'number' && typeof color.green === 'number' && typeof color.blue === 'number' && (typeof color.alpha !== 'number' || typeof color.alpha === 'undefined');
};

var isRgba = function isRgba(color) {
  return typeof color.red === 'number' && typeof color.green === 'number' && typeof color.blue === 'number' && typeof color.alpha === 'number';
};

var isHsl = function isHsl(color) {
  return typeof color.hue === 'number' && typeof color.saturation === 'number' && typeof color.lightness === 'number' && (typeof color.alpha !== 'number' || typeof color.alpha === 'undefined');
};

var isHsla = function isHsla(color) {
  return typeof color.hue === 'number' && typeof color.saturation === 'number' && typeof color.lightness === 'number' && typeof color.alpha === 'number';
};
/**
 * Converts a RgbColor, RgbaColor, HslColor or HslaColor object to a color string.
 * This util is useful in case you only know on runtime which color object is
 * used. Otherwise we recommend to rely on `rgb`, `rgba`, `hsl` or `hsla`.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: toColorString({ red: 255, green: 205, blue: 100 }),
 *   background: toColorString({ red: 255, green: 205, blue: 100, alpha: 0.72 }),
 *   background: toColorString({ hue: 240, saturation: 1, lightness: 0.5 }),
 *   background: toColorString({ hue: 360, saturation: 0.75, lightness: 0.4, alpha: 0.72 }),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${toColorString({ red: 255, green: 205, blue: 100 })};
 *   background: ${toColorString({ red: 255, green: 205, blue: 100, alpha: 0.72 })};
 *   background: ${toColorString({ hue: 240, saturation: 1, lightness: 0.5 })};
 *   background: ${toColorString({ hue: 360, saturation: 0.75, lightness: 0.4, alpha: 0.72 })};
 * `
 *
 * // CSS in JS Output
 * element {
 *   background: "#ffcd64";
 *   background: "rgba(255,205,100,0.72)";
 *   background: "#00f";
 *   background: "rgba(179,25,25,0.72)";
 * }
 */


function toColorString(color) {
  if (typeof color !== 'object') throw new PolishedError(8);
  if (isRgba(color)) return rgba(color);
  if (isRgb(color)) return rgb(color);
  if (isHsla(color)) return hsla(color);
  if (isHsl(color)) return hsl(color);
  throw new PolishedError(8);
}

// Type definitions taken from https://github.com/gcanti/flow-static-land/blob/master/src/Fun.js
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-redeclare
function curried(f, length, acc) {
  return function fn() {
    // eslint-disable-next-line prefer-rest-params
    var combined = acc.concat(Array.prototype.slice.call(arguments));
    return combined.length >= length ? f.apply(this, combined) : curried(f, length, combined);
  };
} // eslint-disable-next-line no-redeclare


function curry(f) {
  // eslint-disable-line no-redeclare
  return curried(f, f.length, []);
}

function guard(lowerBoundary, upperBoundary, value) {
  return Math.max(lowerBoundary, Math.min(upperBoundary, value));
}

/**
 * Returns a number (float) representing the luminance of a color.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: getLuminance('#CCCD64') >= getLuminance('#0000ff') ? '#CCCD64' : '#0000ff',
 *   background: getLuminance('rgba(58, 133, 255, 1)') >= getLuminance('rgba(255, 57, 149, 1)') ?
 *                             'rgba(58, 133, 255, 1)' :
 *                             'rgba(255, 57, 149, 1)',
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${getLuminance('#CCCD64') >= getLuminance('#0000ff') ? '#CCCD64' : '#0000ff'};
 *   background: ${getLuminance('rgba(58, 133, 255, 1)') >= getLuminance('rgba(255, 57, 149, 1)') ?
 *                             'rgba(58, 133, 255, 1)' :
 *                             'rgba(255, 57, 149, 1)'};
 *
 * // CSS in JS Output
 *
 * div {
 *   background: "#CCCD64";
 *   background: "rgba(58, 133, 255, 1)";
 * }
 */

function getLuminance(color) {
  if (color === 'transparent') return 0;
  var rgbColor = parseToRgb(color);

  var _Object$keys$map = Object.keys(rgbColor).map(function (key) {
    var channel = rgbColor[key] / 255;
    return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
  }),
      r = _Object$keys$map[0],
      g = _Object$keys$map[1],
      b = _Object$keys$map[2];

  return parseFloat((0.2126 * r + 0.7152 * g + 0.0722 * b).toFixed(3));
}

/**
 * Sets the lightness of a color to the provided value. The lightness range can be
 * from 0 and 1.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: setLightness(0.2, '#CCCD64'),
 *   background: setLightness('0.75', 'rgba(204,205,100,0.7)'),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${setLightness(0.2, '#CCCD64')};
 *   background: ${setLightness('0.75', 'rgba(204,205,100,0.7)')};
 * `
 *
 * // CSS in JS Output
 * element {
 *   background: "#4d4d19";
 *   background: "rgba(223,224,159,0.7)";
 * }
 */

function setLightness(lightness, color) {
  if (color === 'transparent') return color;
  return toColorString(_extends$c({}, parseToHsl(color), {
    lightness: parseFloat(lightness)
  }));
} // prettier-ignore


var curriedSetLightness = /*#__PURE__*/curry
/* ::<number | string, string, string> */
(setLightness);
var curriedSetLightness$1 = curriedSetLightness;

/**
 * Decreases the opacity of a color. Its range for the amount is between 0 to 1.
 *
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: transparentize(0.1, '#fff');
 *   background: transparentize(0.2, 'hsl(0, 0%, 100%)'),
 *   background: transparentize('0.5', 'rgba(255, 0, 0, 0.8)'),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${transparentize(0.1, '#fff')};
 *   background: ${transparentize(0.2, 'hsl(0, 0%, 100%)')},
 *   background: ${transparentize('0.5', 'rgba(255, 0, 0, 0.8)')},
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "rgba(255,255,255,0.9)";
 *   background: "rgba(255,255,255,0.8)";
 *   background: "rgba(255,0,0,0.3)";
 * }
 */

function transparentize(amount, color) {
  if (color === 'transparent') return color;
  var parsedColor = parseToRgb(color);
  var alpha = typeof parsedColor.alpha === 'number' ? parsedColor.alpha : 1;

  var colorWithAlpha = _extends$c({}, parsedColor, {
    alpha: guard(0, 1, +(alpha * 100 - parseFloat(amount) * 100).toFixed(2) / 100)
  });

  return rgba(colorWithAlpha);
} // prettier-ignore


var curriedTransparentize = /*#__PURE__*/curry
/* ::<number | string, string, string> */
(transparentize);
var curriedTransparentize$1 = curriedTransparentize;

const shadedColors = ['primary', 'secondary', 'grey', 'success', 'info', 'alert', 'error'];
function isShadedColorName(name) {
    return shadedColors.includes(name);
}
class Color {
    constructor(kind, color, shades) {
        this.kind = kind;
        this.value = color;
        this.shades = shades;
    }
    static define(name, value, shades) {
        if (isShadedColorName(name)) {
            if (!shades) {
                throw new Error(`Must define shades for shaded color: ${name}`);
            }
            const shadeMap = new Map();
            Object.entries(shades).forEach(([k, v]) => {
                shadeMap.set(k, v);
            });
            return new Color('shaded', value, shadeMap);
        }
        else {
            return new Color('simple', value);
        }
    }
    toString(s) {
        if (s && this.shades) {
            return this.shades.get(s);
        }
        return this.value;
    }
}

const emphasis = {
    normal: 1, medium: 0.6, low: 0.3
};
const color = (str) => {
    return (props) => {
        if (str === 'inherit' || str === 'currentColor') {
            return str;
        }
        let indicator = str;
        let color = indicator;
        let alpha = 1;
        let invertable = true;
        if (color.includes('@')) {
            const [colorName, colorAlpha] = color.split('@', 2);
            color = colorName;
            alpha = (colorAlpha in emphasis ? emphasis[colorAlpha] : Number(colorAlpha)) || 1;
        }
        if (color.startsWith('!')) {
            invertable = false;
            color = color.substr(1); // remove !
        }
        const [colorName, colorShade] = color.split('.');
        let colorValue;
        if (isShadedColorName(colorName)) {
            colorValue = themeLookup(props.theme, `colors.${colorName}`).toString(colorShade);
        }
        else {
            colorValue = themeLookup(props.theme, `colors.${colorName}`).toString();
        }
        if (alpha < 1) {
            const transparency = 1 - alpha;
            try {
                colorValue = curriedTransparentize$1(transparency, `${colorValue}`);
            }
            catch (e) {
                console.warn('Could not apply transparency to color', color, 'as', colorValue);
                console.error(e);
            }
        }
        const themeIsInverted = themeLookup(props.theme, 'inverted');
        if (themeIsInverted && invertable) {
            try {
                const lightness = getLuminance(`${colorValue}`);
                colorValue = curriedSetLightness$1(1 - lightness, `${colorValue}`);
            }
            catch (e) {
                console.warn('Could not apply inversion to color', color, 'as', colorValue);
                console.error(e);
            }
        }
        return colorValue;
    };
};

const padding = block(rule('padding'), rule('paddingTop'), rule('paddingBottom'), rule('paddingLeft'), rule('paddingRight'));
const border = block(rule('borderWidth'), rule('borderStyle'), rule('borderColor', undefined, { parser: color }), rule('borderTopWidth'), rule('borderTopStyle'), rule('borderTopColor', undefined, { parser: color }), rule('borderBottomWidth'), rule('borderBottomStyle'), rule('borderBottomColor', undefined, { parser: color }), rule('borderLeftWidth'), rule('borderLeftStyle'), rule('borderLeftColor', undefined, { parser: color }), rule('borderRightWidth'), rule('borderRightStyle'), rule('borderRightColor', undefined, { parser: color }), rule('borderRadius'));
const base$5 = block(rule('display'), rule('cursor'), rule('textAlign'), rule('width'), rule('maxWidth'), rule('minWidth'), rule('height'), rule('maxHeight'), rule('minHeight'), rule('padding'), rule('margin'), rule('overflow'), rule('borderRadius'), rule('color', undefined, { parser: color }), rule('backgroundColor', undefined, { parser: color }), rule('focusOutlineColor', undefined, {
    parser: color,
    cssProperty: ':focus-visible.outline-color',
}));
const all = block(base$5, padding, border);

var box = /*#__PURE__*/Object.freeze({
    __proto__: null,
    padding: padding,
    border: border,
    base: base$5,
    all: all
});

function hue(defaultHue, cb) {
    const f = css `
    ${(props) => {
        const hueName = props.$hue || defaultHue;
        return cb(hueName);
    }}
  `;
    return f;
}

const base$4 = block(rule('fontSize', '1.6rem'), rule('fontWeight', 'initial'), rule('color', 'inherit', { parser: color }), rule('textTransform'), rule('textAlign'), rule('textOverflow', 'ellipsis'), rule('overflow'), rule('whiteSpace'), rule('cursor'));
const caps = block(rule('textTransform', 'uppercase'));
const h1 = block(base$4, rule('fontSize', '1.04rem'), rule('fontWeight', '300'), rule('letterSpacing', '-.15rem'));
const h2 = block(base$4, rule('fontSize', '6.4rem'), rule('fontWeight', '300'), rule('letterSpacing', '-.05rem'));
const h3 = block(base$4, rule('fontSize', '4.8rem'), rule('fontWeight', '300'));
const h4 = block(base$4, rule('fontSize', '3.2rem'), rule('fontWeight', 'bold'), rule('letterSpacing', '0.025rem'));
const h5 = block(base$4, rule('fontSize', '2.6rem'), rule('fontWeight', '300'));
const h6 = block(base$4, rule('fontSize', '2.2rem'), rule('fontWeight', '500'), rule('letterSpacing', '0.025rem'));
const subtitle1 = block(base$4, rule('fontSize', '1.8rem'), rule('letterSpacing', '0.015rem'));
const subtitle2 = block(base$4, rule('fontSize', '1.8rem'), rule('fontWeight', '500'), rule('letterSpacing', '0.01rem'));
const body1 = block(base$4, rule('fontSize', '1.8rem'));
const body2 = block(base$4, rule('fontSize', '1.5rem'), rule('letterSpacing', '.025rem'));
const button = block(base$4, caps, rule('fontSize', '1.5rem'), rule('fontWeight', '500'), rule('letterSpacing', '.125rem'));
const caption = block(base$4, rule('fontSize', '1.3rem'), rule('fontWeight', '400'), rule('letterSpacing', '.04rem'));
const overline = block(base$4, caps, rule('fontSize', '1.3rem'), rule('fontWeight', '500'), rule('letterSpacing', '.2rem'));

var text = /*#__PURE__*/Object.freeze({
    __proto__: null,
    base: base$4,
    caps: caps,
    h1: h1,
    h2: h2,
    h3: h3,
    h4: h4,
    h5: h5,
    h6: h6,
    subtitle1: subtitle1,
    subtitle2: subtitle2,
    body1: body1,
    body2: body2,
    button: button,
    caption: caption,
    overline: overline
});

const base$3 = block(button, rule('cursor', 'pointer'), rule('textAlign', 'center'), rule('width', 'fit-content'), rule('height'));
const primary$1 = hue('primary', hue => block(base$3, rule('appearance', 'button'), rule('display', 'inline'), rule('borderRadius', 'buttons.borderRadius'), rule('borderWidth', '1px'), rule('borderStyle', 'solid'), rule('backgroundColor', hue, {
    parser: color,
    hover: `${hue}.darker`,
}), rule('borderColor', hue, {
    parser: color,
    hover: `${hue}.darker`,
}), rule('color', '!white', {
    parser: color,
    hover: '!white'
}), rule('disabledColor', 'grey', {
    parser: color,
    hover: 'grey',
    cssProperty: ':disabled.color',
}), rule('disabledBackgroundColor', 'grey.light', {
    parser: color,
    hover: 'grey.light',
    cssProperty: ':disabled.background-color',
}), rule('disabledBorderColor', 'grey.light', {
    parser: color,
    hover: 'grey.light',
    cssProperty: ':disabled.border-color',
}), rule('padding', '1rem')));
const secondary$1 = hue('primary', hue => block(primary$1, rule('backgroundColor', 'clear', { parser: color }), rule('color', hue, { parser: color }), rule('borderColor', hue, { parser: color }), rule('disabledColor', 'grey', {
    parser: color,
    hover: 'grey',
    cssProperty: ':disabled.color',
}), rule('disabledBorderColor', 'grey.light', {
    parser: color,
    hover: 'grey.light',
    cssProperty: ':disabled.border-color',
})));
const tertiary = hue('primary', hue => block(base$3, rule('display', 'inline'), rule('backgroundColor', 'clear', { parser: color }), rule('borderStyle', 'none'), rule('color', hue, {
    parser: color,
    hover: `${hue}.darker`,
}), rule('disabledColor', 'grey', {
    parser: color,
    hover: 'grey',
    cssProperty: ':disabled.color',
})));

var buttons = /*#__PURE__*/Object.freeze({
    __proto__: null,
    base: base$3,
    primary: primary$1,
    secondary: secondary$1,
    tertiary: tertiary
});

const base$2 = block(rule('padding'), rule('textAlign'), rule('width'), rule('height'), rule('maxWidth'), rule('maxHeight'), rule('minWidth'), rule('minHeight'), rule('display'), rule('overflow'), rule('whiteSpace'), rule('cursor'), rule('borderRadius'), rule('transform'), rule('borderStyle'), rule('borderWidth'), rule('color', undefined, {
    parser: color,
}), rule('borderColor', undefined, { parser: color }), rule('backgroundColor', 'clear', {
    parser: color,
}), rule('focusOutlineColor', undefined, {
    parser: color,
    cssProperty: ':focus.outline-color',
}));
const position = block(base$2, rule('position'), rule('top'), rule('left'), rule('bottom'), rule('right'), rule('zIndex'));
const grid = block(base$2, rule('display', 'grid'), rule('gridTemplateColumns'), rule('gridTemplateRows'), rule('gridAutoColumns'), rule('gridAutoRows'), rule('gridGap'), rule('alignItems'), rule('justifyItems'), rule('alignContent'), rule('justifyContent'));
const gridItem = block(base$2, rule('gridColumnStart'), rule('gridColumnEnd'), rule('gridRowStart'), rule('gridRowEnd'), rule('justifySelf'), rule('alignSelf'));
const gridItemGrid = block(grid, gridItem);
const panel = block(rule('textAlign'), rule('borderStyle', 'solid'), rule('borderWidth', '1px'), rule('borderColor', 'grey.light', { parser: color }), rule('borderRightStyle', undefined, { media: { mobile: 'none' } }), rule('borderLeftStyle', undefined, { media: { mobile: 'none' } }), rule('backgroundColor', 'white', {
    parser: color,
}), rule('padding', 'panel.padding', {
    media: {
        mobile: 'panel.mobile.padding',
    }
}), rule('borderRadius', 'panel.borderRadius', {
    media: {
        mobile: '0',
    }
}));

var containers = /*#__PURE__*/Object.freeze({
    __proto__: null,
    base: base$2,
    position: position,
    grid: grid,
    gridItem: gridItem,
    gridItemGrid: gridItemGrid,
    panel: panel
});

function isPercent(s) {
    return s === '0' || s.endsWith('%');
}
// function isColorValue(s:string):s is ColorValue {
//   return !isPercent(s)
// }
function parseStop(stop, props) {
    if (stop.includes(' ')) {
        return stop.split(' ').map(s => parseStop(s, props)).join(' ');
    }
    if (isPercent(stop)) {
        return stop;
    }
    return color(stop)(props);
}
const linearGradient$1 = (gradient) => {
    if (Array.isArray(gradient)) {
        return (props) => `linear-gradient(${gradient.map(s => parseStop(s, props)).join(', ')})`;
    }
    return (props) => `linear-gradient(${gradient.direction}, ${gradient.stops.map(s => parseStop(s, props)).join(', ')})`;
};

const opacity = block(rule('opacity'));
const linearGradient = block(rule('linearGradient', ['primary', 'primary@0'], {
    parser: linearGradient$1,
    cssProperty: 'background-image',
}));

var effects = /*#__PURE__*/Object.freeze({
    __proto__: null,
    opacity: opacity,
    linearGradient: linearGradient
});

const dropShadow$1 = (ds) => {
    const [x, y, blur, c] = ds;
    return (props) => `drop-shadow(${x} ${y} ${blur} ${color(c)(props)})`;
};

const dropShadow = block(rule('dropShadow', ['2px', '2px', '2px', 'black@.22'], {
    parser: dropShadow$1,
    cssProperty: 'filter',
}));

var filters = /*#__PURE__*/Object.freeze({
    __proto__: null,
    dropShadow: dropShadow
});

const input = block(border, rule('width', '100%'), rule('padding', '2.5rem .4rem 1rem'), rule('color', 'black', { parser: color }), rule('backgroundColor', 'clear', { parser: color }), rule('placeholderColor', 'grey', {
    parser: color,
    cssProperty: '::placeholder.color',
}), rule('focusOutlineColor', 'primary', {
    parser: color,
    cssProperty: ':focus-visible.outline-color',
}), rule('disabledColor', 'grey.light', {
    parser: color,
    hover: 'grey.light',
    cssProperty: ':disabled.color',
}));
const select = block(border, rule('width', '100%'), rule('padding', '2.5rem .4rem 1rem'), rule('color', 'black', { parser: color }), rule('backgroundColor', 'clear', { parser: color }), rule('placeholderColor', 'grey', {
    parser: color,
    cssProperty: '::placeholder.color',
}), rule('focusOutlineColor', 'primary', {
    parser: color,
    cssProperty: ':focus-visible.outline-color',
}), rule('disabledColor', 'grey.light', {
    parser: color,
    hover: 'grey.light',
    cssProperty: ':disabled.color',
}), rule('mobileHeight', '3.3rem', {
    cssProperty: '@supports (-webkit-overflow-scrolling: touch).height'
}), rule('appearance', 'none'));

var form = /*#__PURE__*/Object.freeze({
    __proto__: null,
    input: input,
    select: select
});

const base$1 = block(rule('width', 'fit-content'), rule('height'), rule('maxWidth', '100%'), rule('maxHeight', '100%'), rule('color', 'inherit', { parser: color }), rule('cursor'));

var icons$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    base: base$1
});

const base = block(rule('width', '0'), rule('height', '0'));
const inline = block(base, rule('display', 'inline-block'));

var spacers = /*#__PURE__*/Object.freeze({
    __proto__: null,
    base: base,
    inline: inline
});

const makeComponentFor = guitar({
    box,
    buttons,
    containers,
    effects,
    filters,
    form,
    icons: icons$1,
    spacers,
    text,
});

const Box = {
    Model: makeComponentFor('div', 'box.all'),
    Simple: makeComponentFor('div', 'box.base'),
    Padding: makeComponentFor('div', 'box.padding'),
    Border: makeComponentFor('div', 'box.border'),
};

function buttonThemesOn(element) {
    return {
        Primary: makeComponentFor(element, 'buttons.primary'),
        Secondary: makeComponentFor(element, 'buttons.secondary'),
        Tertiary: makeComponentFor(element, 'buttons.tertiary'),
    };
}
const Button = buttonThemesOn('button');
const subs = {
    A: buttonThemesOn('a'),
    Div: buttonThemesOn('div'),
    Link: buttonThemesOn(Link),
};
Object.assign(Button, subs);

const Base = makeComponentFor('div', 'containers.base');
const Grid = makeComponentFor('div', 'containers.grid');
const GridItem = makeComponentFor('div', 'containers.gridItem');
const GridItemGrid = makeComponentFor('div', 'containers.gridItemGrid');
const Panel = makeComponentFor('div', 'containers.panel');
const Position = makeComponentFor('div', 'containers.position');
const Container = {
    Base,
    Grid,
    GridItem,
    GridItemGrid,
    Panel,
    Position
};

const Effect = {
    LinearGradient: makeComponentFor('div', 'effects.linearGradient'),
    Opacity: makeComponentFor('div', 'effects.opacity'),
};

const Filter = {
    DropShadow: makeComponentFor('div', 'filters.dropShadow'),
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

var jsxRuntime = {exports: {}};

var reactJsxRuntime_production_min = {};

/** @license React v16.14.0
 * react-jsx-runtime.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var f=React__default,g=60103;reactJsxRuntime_production_min.Fragment=60107;if("function"===typeof Symbol&&Symbol.for){var h=Symbol.for;g=h("react.element");reactJsxRuntime_production_min.Fragment=h("react.fragment");}var m=f.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.ReactCurrentOwner,n=Object.prototype.hasOwnProperty,p={key:!0,ref:!0,__self:!0,__source:!0};
function q(c,a,k){var b,d={},e=null,l=null;void 0!==k&&(e=""+k);void 0!==a.key&&(e=""+a.key);void 0!==a.ref&&(l=a.ref);for(b in a)n.call(a,b)&&!p.hasOwnProperty(b)&&(d[b]=a[b]);if(c&&c.defaultProps)for(b in a=c.defaultProps,a)void 0===d[b]&&(d[b]=a[b]);return {$$typeof:g,type:c,key:e,ref:l,props:d,_owner:m.current}}reactJsxRuntime_production_min.jsx=q;reactJsxRuntime_production_min.jsxs=q;

{
  jsxRuntime.exports = reactJsxRuntime_production_min;
}

var _g$6;

function _extends$b() { _extends$b = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b.apply(this, arguments); }

var SvgBank = function SvgBank(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "5 5 26 26",
    "data-name": "RoadieUI_icon_Bank"
  }, props), _g$6 || (_g$6 = /*#__PURE__*/React.createElement("g", {
    transform: "translate(-59.000000, -115.000000) translate(53.000000, 109.000000)"
  }, /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    fillOpacity: 0,
    d: "M10.098 0L0 5.645 0 7.902 20.195 7.902 20.195 5.645zM15.146 18.146L18.512 18.146 18.512 10.244 15.146 10.244zM8.415 18.146L11.78 18.146 11.78 10.244 8.415 10.244zM1.683 18.146L5.049 18.146 5.049 10.244 1.683 10.244zM0 24L20.195 24 20.195 20.488 0 20.488z",
    transform: "translate(6.000000, 6.000000) translate(8.000000, 6.000000)"
  }))));
};

var _g$5;

function _extends$a() { _extends$a = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a.apply(this, arguments); }

var SvgCheckmarkCircle = function SvgCheckmarkCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a({
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    viewBox: "0 0 14 14",
    "data-name": "RoadieUI_icon_CheckmarkCircle"
  }, props), _g$5 || (_g$5 = /*#__PURE__*/React.createElement("g", {
    fillRule: "evenodd",
    className: "icon-fill"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M7 .333C3.32.333.333 3.32.333 7c0 3.68 2.987 6.667 6.667 6.667 3.68 0 6.667-2.987 6.667-6.667C13.667 3.32 10.68.333 7 .333zm-1.333 10L2.333 7l.94-.94 2.394 2.387 5.06-5.06.94.946-6 6z"
  }))));
};

var _g$4;

function _extends$9() { _extends$9 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9.apply(this, arguments); }

var SvgChevronDoubleRight = function SvgChevronDoubleRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "-3 0 24 24",
    "data-name": "RoadieUI_icon_ChevronDoubleRight"
  }, props), _g$4 || (_g$4 = /*#__PURE__*/React.createElement("g", {
    className: "icon-fill",
    fillRule: "nonzero"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M9.683 23.741a1.44 1.44 0 0 1-.36-2l6.583-9.463a.48.48 0 0 0 0-.549L9.323 2.262A1.44 1.44 0 0 1 11.688.618l6.582 9.463a3.372 3.372 0 0 1 0 3.837l-6.582 9.463a1.44 1.44 0 0 1-2.005.36z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4.96.892l6.585 9.463c.684.99.684 2.3 0 3.29L4.96 23.107a.961.961 0 0 1-.788.411H.96a.96.96 0 0 1-.788-1.508l6.583-9.463c.23-.33.23-.769 0-1.1L.172 1.988A.96.96 0 0 1 .96.48h3.213a.96.96 0 0 1 .788.412z"
  }))));
};

var _path$3;

function _extends$8() { _extends$8 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8.apply(this, arguments); }

var SvgChevronRight = function SvgChevronRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8({
    viewBox: "-3 -0.2 15 15",
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    "data-name": "RoadieUI_icon_ChevronRight"
  }, props), _path$3 || (_path$3 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M7.07106781,4.07106781 L7.07106781,12.0710678 L5.07106781,12.0710678 L5.07106781,4.07106781 L-2.92893219,4.07106781 L-2.92893219,2.07106781 L7.07106781,2.07106781 L7.07106781,4.07106781 Z",
    transform: "translate(2.071068, 7.071068) rotate(-315.000000) translate(-2.071068, -7.071068)"
  })));
};

var _path$2;

function _extends$7() { _extends$7 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7.apply(this, arguments); }

var SvgChevronLeft = function SvgChevronLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7({
    viewBox: "-3 -0.2 15 15",
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    "data-name": "RoadieUI_icon_ChevronLeft"
  }, props), _path$2 || (_path$2 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M7.07106781,4.07106781 L7.07106781,12.0710678 L5.07106781,12.0710678 L5.07106781,4.07106781 L-2.92893219,4.07106781 L-2.92893219,2.07106781 L7.07106781,2.07106781 L7.07106781,4.07106781 Z",
    transform: "translate(6.271068, 7.071068) rotate(-135.000000) translate(-2.071068, -7.071068)"
  })));
};

var _path$1;

function _extends$6() { _extends$6 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6.apply(this, arguments); }

var SvgLoader = function SvgLoader(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6({
    id: "loader-1",
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    x: "0px",
    y: "0px",
    viewBox: "0 0 50 50",
    style: {
      enableBackground: "new 0 0 50 50"
    },
    xmlSpace: "preserve",
    "data-name": "RoadieUI_icon_Loader"
  }, props), _path$1 || (_path$1 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z"
  }, /*#__PURE__*/React.createElement("animateTransform", {
    attributeType: "xml",
    attributeName: "transform",
    type: "rotate",
    from: "0 25 25",
    to: "360 25 25",
    dur: "0.6s",
    repeatCount: "indefinite"
  }))));
};

var _g$3;

function _extends$5() { _extends$5 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5.apply(this, arguments); }

var SvgLogo = function SvgLogo(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5({
    viewBox: "0 0 100 100",
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    "data-name": "RoadieUI_icon_Logo"
  }, props), _g$3 || (_g$3 = /*#__PURE__*/React.createElement("g", {
    stroke: "none",
    strokeWidth: 1,
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("g", {
    fillRule: "nonzero",
    className: "icon-fill"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M49.66,0 C22.6008458,0.00584091794 0.522881589,21.6658976 0,48.72 L29.52,48.72 L29.52,27.4 C29.5172793,27.208223 29.5922623,27.0235032 29.7278827,26.8878827 C29.8635032,26.7522623 30.048223,26.6772793 30.24,26.68 L52.29,26.68 C72.99,26.68 71.36,41.1 71.36,41.1 C71.36,49.47 66.04,53.17 58.92,53.17 L0.11,53.17 C0.11,53.72 0.18,54.27 0.24,54.82 L56.88,54.82 C58.7851203,54.8685697 60.580331,55.7225667 61.82,57.17 L62.29,57.82 L62.29,57.82 L73.83,73.82 L59.13,73.82 C58.6874075,73.8013798 58.2780158,73.580087 58.02,73.22 L49.11,59.78 C48.9915844,59.5806327 48.8542869,59.3931044 48.7,59.22 L43.29,59.22 C43.0173822,59.3361252 42.8403238,59.6036802 42.84,59.9 L42.84,73.04 C42.8427207,73.231777 42.7677377,73.4164968 42.6321173,73.5521173 C42.4964968,73.6877377 42.311777,73.7627207 42.12,73.76 L30.29,73.76 C30.098223,73.7627207 29.9135032,73.6877377 29.7778827,73.5521173 C29.6422623,73.4164968 29.5672793,73.231777 29.57,73.04 L29.57,59.22 L0.9,59.22 C5.93028802,84.9279445 30.0574534,102.312921 56.0359691,98.9485594 C82.0144848,95.584198 100.917068,72.6266311 99.2325987,46.4853846 C97.5481297,20.3441381 75.8554614,0.00241595179 49.66,-7.10542736e-15 L49.66,0 Z",
    id: "Path"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M51.67,48.72 C56.21,48.72 58.83,46.52 58.83,43.01 L58.83,42.87 C58.83,39.09 56.08,37.16 51.61,37.16 L43.61,37.16 C43.418223,37.1572793 43.2335032,37.2322623 43.0978827,37.3678827 C42.9622623,37.5035032 42.8872793,37.688223 42.89,37.88 L42.89,48 C42.8872793,48.191777 42.9622623,48.3764968 43.0978827,48.5121173 C43.2335032,48.6477377 43.418223,48.7227207 43.61,48.72 L51.67,48.72 Z",
    id: "Path"
  })))));
};

var _path, _path2, _path3, _path4, _path5, _path6, _path7, _path8, _path9, _path10, _path11, _path12, _path13, _path14;

function _extends$4() { _extends$4 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4.apply(this, arguments); }

var SvgRoadieUpsCompany = function SvgRoadieUpsCompany(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 375.04 103.05",
    "data-name": "RoadieUI_icon_RoadieUpsCompany"
  }, props), _path || (_path = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M127.53,44.86c0,2.86-2.13,4.65-5.83,4.65h-6.58a.57.57,0,0,1-.58-.58V40.69a.57.57,0,0,1,.58-.58h6.53c3.64,0,5.88,1.56,5.88,4.64Zm12.21,25h0l-9.35-13.66c4.87-2.07,8.06-6,8.06-12v-.11A12.19,12.19,0,0,0,135,35c-2.63-2.68-6.78-4.25-12.77-4.25H104.26a.57.57,0,0,0-.58.58v38a.57.57,0,0,0,.58.58H114a.57.57,0,0,0,.59-.58V58.61a.57.57,0,0,1,.58-.59h4.12s.06,0,.38.49l7.25,10.9a1.16,1.16,0,0,0,.91.48Zm42.38-19.48c0,5.65-4,10.47-10,10.47s-10-4.93-10-10.58v-.12c0-5.65,4-10.47,9.91-10.47s10.07,4.93,10.07,10.59Zm11.09-.11v-.12c0-11.25-9-20.27-21.05-20.27S151,39,151,50.3v.11c0,11.25,9,20.27,21.06,20.27s21.16-9.13,21.16-20.38m34.38,3.59s.22.55-.37.55h-7.61s-.59,0-.38-.55l4-10s.21-.54.43,0Zm6,15.46a.91.91,0,0,0,.81.54h10.47s.58,0,.36-.54L229,31a.92.92,0,0,0-.81-.54h-9.31a.92.92,0,0,0-.81.54L201.83,69.35s-.22.54.36.54h10.25a.9.9,0,0,0,.8-.54l2.37-5.91a.9.9,0,0,1,.8-.54h14a.92.92,0,0,1,.81.54Zm50.17-19c0,6.33-4.31,9.91-10.81,9.91H269.1a.57.57,0,0,1-.58-.58V40.91a.57.57,0,0,1,.58-.58h3.84c6.5,0,10.81,3.64,10.81,9.91Zm11.14-.17v-.11c0-11.31-8.12-19.37-22.12-19.37H258.24a.57.57,0,0,0-.58.58v38a.57.57,0,0,0,.58.58h14.31c14.11,0,22.34-8.4,22.34-19.71m14.23,19.13a.57.57,0,0,0,.58.58h9.75a.57.57,0,0,0,.58-.58v-38a.57.57,0,0,0-.58-.58H309.7a.57.57,0,0,0-.58.58Zm37.82-8.66a.57.57,0,0,1-.59-.58V55a.57.57,0,0,1,.59-.58h17.64a.58.58,0,0,0,.59-.59V46.46a.58.58,0,0,0-.59-.59H346.94a.57.57,0,0,1-.59-.58V40.52a.57.57,0,0,1,.59-.58h19.6a.58.58,0,0,0,.59-.59V31.28a.57.57,0,0,0-.59-.58H336.19a.57.57,0,0,0-.59.58v38a.57.57,0,0,0,.59.58h30.63a.57.57,0,0,0,.59-.58V61.24a.58.58,0,0,0-.59-.59Z"
  })), _path2 || (_path2 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M49.23,7.82A42.46,42.46,0,0,0,6.78,49.47H32V31.23a.6.6,0,0,1,.61-.62H51.48c17.7,0,16.31,12.33,16.31,12.33,0,7.15-4.56,10.31-10.64,10.31H6.87c0,.48.06,1,.11,1.42H55.41a5.73,5.73,0,0,1,4.22,2l.4.55h0l9.86,13.64H57.33a1.23,1.23,0,0,1-.95-.51L48.77,58.92a3.21,3.21,0,0,0-.36-.47h-4.6a.63.63,0,0,0-.38.58V70.27a.6.6,0,0,1-.62.61H32.63a.6.6,0,0,1-.61-.61V58.45H7.55A42.48,42.48,0,1,0,49.23,7.82Z"
  })), _path3 || (_path3 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M51,49.47c3.89,0,6.12-1.88,6.12-4.88v-.12c0-3.23-2.35-4.88-6.17-4.88H44a.6.6,0,0,0-.61.62v8.65a.6.6,0,0,0,.61.61Z"
  })), _path4 || (_path4 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M197.91,77.66h2.38l6.45,14.76H204l-1.49-3.54H195.6l-1.5,3.54h-2.64Zm3.68,8.94-2.53-5.86-2.51,5.86Z"
  })), _path5 || (_path5 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M215.92,86.2V77.77h2.58V86.1c0,2.72,1.4,4.18,3.7,4.18s3.68-1.38,3.68-4.08V77.77h2.58v8.31c0,4.37-2.47,6.57-6.3,6.57S215.92,90.45,215.92,86.2Z"
  })), _path6 || (_path6 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M232.58,77.77h5.77c3.42,0,5.57,1.94,5.57,4.9v0c0,3.28-2.64,5-5.86,5h-2.91v4.71h-2.57Zm5.56,7.62c2,0,3.17-1.09,3.17-2.62v0c0-1.72-1.24-2.62-3.17-2.62h-3v5.28Z"
  })), _path7 || (_path7 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M246,90.28l1.55-1.84a6.71,6.71,0,0,0,4.62,1.91c1.59,0,2.6-.74,2.6-1.85v0c0-1-.59-1.61-3.31-2.24-3.12-.75-4.88-1.67-4.88-4.37v0c0-2.51,2.1-4.25,5-4.25a8.12,8.12,0,0,1,5.31,1.84l-1.38,1.95a6.63,6.63,0,0,0-4-1.51c-1.5,0-2.38.77-2.38,1.74v0c0,1.13.67,1.63,3.47,2.3,3.1.75,4.71,1.86,4.71,4.29v0c0,2.74-2.15,4.38-5.23,4.38A9,9,0,0,1,246,90.28Z"
  })), _path8 || (_path8 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M266.61,85.13v0a7.41,7.41,0,0,1,7.56-7.57,7.62,7.62,0,0,1,5.73,2.28l-1.65,1.9a5.82,5.82,0,0,0-4.1-1.82,4.92,4.92,0,0,0-4.84,5.17v0a5,5,0,0,0,4.84,5.21,5.72,5.72,0,0,0,4.2-1.9L280,90.07a7.58,7.58,0,0,1-5.95,2.6A7.37,7.37,0,0,1,266.61,85.13Z"
  })), _path9 || (_path9 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M282,87v0a5.89,5.89,0,0,1,11.78,0v0a5.78,5.78,0,0,1-5.92,5.76A5.72,5.72,0,0,1,282,87Zm9.25,0v0a3.41,3.41,0,0,0-3.39-3.55,3.32,3.32,0,0,0-3.33,3.51v0a3.43,3.43,0,0,0,3.37,3.56A3.33,3.33,0,0,0,291.2,87Z"
  })), _path10 || (_path10 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M296.62,81.37h2.53V83a4,4,0,0,1,3.37-1.9,3.48,3.48,0,0,1,3.29,1.94,4.51,4.51,0,0,1,3.74-1.94c2.41,0,3.88,1.53,3.88,4.23v7h-2.54V86.14c0-1.76-.81-2.7-2.24-2.7s-2.36,1-2.36,2.74v6.24h-2.53v-6.3c0-1.72-.84-2.68-2.24-2.68s-2.37,1-2.37,2.76v6.22h-2.53Z"
  })), _path11 || (_path11 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M316.9,81.37h2.53v1.84a4.43,4.43,0,0,1,3.75-2.07c2.61,0,5.15,2.07,5.15,5.73v0c0,3.66-2.51,5.74-5.15,5.74a4.51,4.51,0,0,1-3.75-2v5.07H316.9Zm8.85,5.54v0a3.27,3.27,0,0,0-3.16-3.53,3.33,3.33,0,0,0-3.22,3.53v0a3.32,3.32,0,0,0,3.22,3.54A3.23,3.23,0,0,0,325.75,86.91Z"
  })), _path12 || (_path12 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M338,92.42V91.06a4.45,4.45,0,0,1-3.58,1.59c-2.09,0-3.93-1.2-3.93-3.41v0c0-2.44,1.9-3.62,4.48-3.62A9.33,9.33,0,0,1,338,86v-.21c0-1.52-1-2.36-2.72-2.36a7.67,7.67,0,0,0-3.16.69l-.69-2a9.54,9.54,0,0,1,4.21-.92c3.26,0,4.85,1.72,4.85,4.67v6.55Zm.06-4.63a7.27,7.27,0,0,0-2.53-.44c-1.59,0-2.53.65-2.53,1.72v0c0,1,.94,1.63,2.13,1.63,1.68,0,2.93-.94,2.93-2.32Z"
  })), _path13 || (_path13 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M343.89,81.37h2.53v1.71a4,4,0,0,1,3.45-1.94c2.49,0,3.94,1.67,3.94,4.25v7h-2.54V86.14c0-1.72-.85-2.7-2.36-2.7a2.45,2.45,0,0,0-2.49,2.74v6.24h-2.53Z"
  })), _path14 || (_path14 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M356.29,95.16l.85-1.88a3.18,3.18,0,0,0,1.49.41c.71,0,1.15-.31,1.57-1.27l-4.6-11h2.7l3.13,8.2,2.87-8.2h2.64l-4.4,11.36c-.88,2.26-1.86,3.1-3.64,3.1A5.1,5.1,0,0,1,356.29,95.16Z"
  })));
};

var _g$2;

function _extends$3() { _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }

var SvgShippingBox = function SvgShippingBox(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 40 40",
    "data-name": "RoadieUI_icon_ShippingBox"
  }, props), _g$2 || (_g$2 = /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd",
    className: "icon-stroke",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 1.583
  }, /*#__PURE__*/React.createElement("path", {
    d: "M39.167 10.233c0-.663-.393-1.263-1-1.528l-17.5-7.61a1.677 1.677 0 0 0-1.334 0l-17.5 7.61c-.607.265-1 .865-1 1.528v19.462c0 .673.405 1.28 1.025 1.538l18.309 7.629c.428.178.91.17 1.333-.022l16.69-7.59c.595-.27.977-.863.977-1.517v-19.5zM20.833 17.473L1.142 9.268M20.833 17.473l18.032-8.196M11.978 4.298l18.502 8.797"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M15.833 21.56a.833.833 0 0 0-.5-.763L7 17.15a.833.833 0 0 0-1.167.765v6.513c0 .332.197.631.5.764l8.334 3.646a.833.833 0 0 0 1.166-.763V21.56zM20.833 39.043v-21.57"
  }))));
};

var _g$1;

function _extends$2() { _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }

var SvgSteeringWheel = function SvgSteeringWheel(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 40 40",
    "data-name": "RoadieUI_icon_SteeringWheel"
  }, props), _g$1 || (_g$1 = /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd",
    className: "icon-stroke",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 1.583
  }, /*#__PURE__*/React.createElement("circle", {
    cx: 20,
    cy: 20,
    r: 19.167
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.37 16.227a46.122 46.122 0 0 1 27.26 0"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: 20,
    cy: 20,
    r: 14.167
  }), /*#__PURE__*/React.createElement("circle", {
    cx: 20,
    cy: 20,
    r: 2.5
  }), /*#__PURE__*/React.createElement("path", {
    d: "M5.887 20.862c.258-.019.518-.029.78-.029 5.983 0 10.833 4.85 10.833 10.834 0 .752-.08 1.501-.238 2.236M22.732 33.903a9.603 9.603 0 0 1-.232-2.236c0-5.983 4.85-10.834 10.833-10.834.3 0 .512.045.804.07"
  }))));
};

var _g;

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

var SvgTooltipNotch = function SvgTooltipNotch(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    viewBox: "0 0 30 30",
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    "data-name": "RoadieUI_icon_TooltipNotch"
  }, props), _g || (_g = /*#__PURE__*/React.createElement("g", {
    className: "icon-fill"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M0,0 C 15,25 15,25 30,0 Z"
  }))));
};

var _polygon;

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

var SvgTriangleDown = function SvgTriangleDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    viewBox: "0 0 531.74 531.74",
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    "data-name": "RoadieUI_icon_TriangleDown"
  }, props), _polygon || (_polygon = /*#__PURE__*/React.createElement("polygon", {
    className: "icon-fill",
    points: "530.874,0.5 265.87,459.5 0.866,0.5 "
  })));
};

const colorize = (c) => {
    return styled(c) `
    .icon-stroke {
      stroke: currentColor;
    }
    .icon-fill {
      fill: currentColor;
    }
  `;
};
const icons = {
    Bank: makeComponentFor(colorize(SvgBank), 'icons.base'),
    CheckmarkCircle: makeComponentFor(colorize(SvgCheckmarkCircle), 'icons.base'),
    ChevronDoubleRight: makeComponentFor(colorize(SvgChevronDoubleRight), 'icons.base'),
    ChevronRight: makeComponentFor(colorize(SvgChevronRight), 'icons.base'),
    ChevronLeft: makeComponentFor(colorize(SvgChevronLeft), 'icons.base'),
    Loader: makeComponentFor(colorize(SvgLoader), 'icons.base'),
    Logo: makeComponentFor(colorize(SvgLogo), 'icons.base'),
    RoadieUpsCompany: makeComponentFor(colorize(SvgRoadieUpsCompany), 'icons.base'),
    ShippingBox: makeComponentFor(colorize(SvgShippingBox), 'icons.base'),
    SteeringWheel: makeComponentFor(colorize(SvgSteeringWheel), 'icons.base'),
    TooltipNotch: makeComponentFor(colorize(SvgTooltipNotch), 'icons.base'),
    TriangleDown: makeComponentFor(colorize(SvgTriangleDown), 'icons.base'),
};
const Icon = props => {
    const { svg } = props, $props = __rest(props, ["svg"]);
    const SvgIcon = icons[svg];
    return jsxRuntime.exports.jsx(SvgIcon, Object.assign({}, $props), void 0);
};
Object.assign(Icon, icons);

const Spacer = makeComponentFor('div', 'spacers.base');
const Inline = makeComponentFor('span', 'spacers.inline');
Object.assign(Spacer, {
    Inline,
});

function textThemesOnElement(element) {
    return {
        Button: makeComponentFor(element, 'text.button'),
        Caption: makeComponentFor(element, 'text.caption'),
        H1: makeComponentFor(element, 'text.h1'),
        H2: makeComponentFor(element, 'text.h2'),
        H3: makeComponentFor(element, 'text.h3'),
        H4: makeComponentFor(element, 'text.h4'),
        H5: makeComponentFor(element, 'text.h5'),
        H6: makeComponentFor(element, 'text.h6'),
        Caps: makeComponentFor(element, 'text.caps'),
        Subtitle1: makeComponentFor(element, 'text.subtitle1'),
        Subtitle2: makeComponentFor(element, 'text.subtitle2'),
        Body1: makeComponentFor(element, 'text.body1'),
        Body2: makeComponentFor(element, 'text.body2'),
        Overline: makeComponentFor(element, 'text.overline'),
    };
}
const Text = textThemesOnElement('div');
const Span = textThemesOnElement('span');

const primary = Color.define('primary', '#00BBBB', {
    lighter: '#A5E4E4',
    light: '#A3DBDF',
    dark: '#0B8888',
    darker: '#004A4A',
});
const secondary = Color.define('secondary', '#0E46C8', {
    lighter: '#88AAD9',
    light: '#5D82D8',
    dark: '#0534A2',
    darker: '#04287E',
});
const grey = Color.define('grey', '#A8A8A8', {
    lighter: '#EAEAEA',
    light: '#D2D2D2',
    dark: '#7A7A7A',
    darker: '#4C4C4C',
});
const info = Color.define('info', '#258afb', {
    lighter: '#d3e8fe',
    light: '#92c5fd',
    dark: '#1a61b0',
    darker: '#0b294b',
});
const success = Color.define('success', '#72bf44', {
    lighter: '#e3f2da',
    light: '#b9dfa2',
    dark: '#508630',
    darker: '#223914',
});
const alert = Color.define('alert', '#fdb913', {
    lighter: '#fff1d0',
    light: '#fedc89',
    dark: '#b1820d',
    darker: '#4c3706',
});
const error = Color.define('error', '#e34025', {
    lighter: '#f9d9d3',
    light: '#f1a092',
    dark: '#9f2d1a',
    darker: '#44130b',
});
const white = Color.define('white', 'white');
const black = Color.define('black', 'black');
const clear = Color.define('clear', 'rgba(0,0,0,0)');
const star = Color.define('star', '#efcd14');
const facebookBlue = Color.define('facebookBlue', '#3B5998');
const background = Color.define('background', '#f6f6f6');

var colors = /*#__PURE__*/Object.freeze({
    __proto__: null,
    primary: primary,
    secondary: secondary,
    grey: grey,
    info: info,
    success: success,
    alert: alert,
    error: error,
    white: white,
    black: black,
    clear: clear,
    star: star,
    facebookBlue: facebookBlue,
    background: background
});

const theme = {
    colors,
    buttons: {
        borderRadius: '5px',
    },
    panel: {
        padding: '3.2rem 4rem',
        borderRadius: '5px',
        mobile: {
            padding: '2.4rem',
        }
    },
};

const dark = Object.assign(Object.assign({}, theme), { inverted: true });

const AutoGridContext = createContext({ columnCount: 0, columnWidth: 0, gridGap: [0, 0] });
const useClientRect = (ref) => {
    const [rect, setRect] = useState(ref.current ? ref.current.getBoundingClientRect() : null);
    useLayoutEffect(() => {
        const { current } = ref;
        if (!current) {
            return;
        }
        const observer = new ResizeObserver(entries => {
            const element = entries[0];
            setRect(element.target.getBoundingClientRect());
        });
        observer.observe(current);
        return () => {
            observer.unobserve(current);
        };
    }, [ref]);
    return rect;
};
const AutoGrid$1 = props => {
    const { minColumnWidth = 250, maxColumns = 3, children } = props;
    const gridGap = Array.isArray(props.gridGap) ? props.gridGap : [props.gridGap || 0, props.gridGap || 0];
    const [gridGapVertical, gridGapHorizontal] = gridGap;
    const ref = useRef(null);
    const rect = useClientRect(ref);
    const getColumns = useCallback((wanted) => {
        let result = [wanted, 0];
        if (wanted === 1 || !rect) {
            return result;
        }
        else if (rect) {
            const columnGap = 30 * (wanted - 1);
            const columnWidth = (rect.width - columnGap) / wanted;
            if (columnWidth >= minColumnWidth) {
                result = [wanted, columnWidth];
            }
            else {
                result = getColumns(wanted - 1);
            }
        }
        return result;
    }, [minColumnWidth, rect]);
    const [columnCount, columnWidth] = getColumns(maxColumns);
    const cols = Array.from({ length: columnCount }).map(() => '1fr');
    cols[cols.length - 1] = '1fr';
    const template = '[start] ' + cols.join(' ') + ' [end]';
    return (jsxRuntime.exports.jsx(AutoGridContext.Provider, Object.assign({ value: { columnCount, columnWidth, gridGap } }, { children: jsxRuntime.exports.jsx(Container.Grid, Object.assign({ ref: ref, "$gridTemplateColumns": template, "$gridGap": `${gridGapVertical}px ${gridGapHorizontal}px`, "$alignItems": "end" }, { children: children }), void 0) }), void 0));
};

const AutoGridCell = props => {
    const { minWidth = 0, columnSpan = 0, columnStart, columnEnd, rowStart, rowEnd, children } = props;
    const grid = useContext(AutoGridContext);
    if (columnStart && columnEnd) {
        if (columnSpan || minWidth) {
            throw new Error(`
        Cannot set columnSpan or minWidth when setting columnStart and columnEnd. 
        Must remove either columnStart or remove columnEnd if using minWidth or columnSpan.
      `);
        }
    }
    const getColumns = useCallback((wanted) => {
        // the total width of the columns + the gaps to span the wanted length
        const totalWidth = (wanted * grid.columnWidth) + (grid.gridGap[1] * (wanted - 1));
        if (totalWidth < minWidth && wanted < grid.columnCount) {
            return getColumns(wanted + 1);
        }
        return wanted;
    }, [grid, minWidth]);
    const span = getColumns(Math.min(columnSpan || 1, grid.columnCount));
    let start = columnStart, end = columnEnd;
    if (columnEnd && !columnStart) {
        start = `span ${span}`;
    }
    else if (!columnEnd) {
        end = `span ${span}`;
    }
    return (jsxRuntime.exports.jsx(Container.GridItem, Object.assign({ "$gridColumnStart": start, "$gridColumnEnd": end, "$gridRowStart": rowStart, "$gridRowEnd": rowEnd }, { children: children }), void 0));
};

const FullRow = props => (jsxRuntime.exports.jsx(AutoGridCell, Object.assign({ columnStart: "start", columnEnd: "end" }, { children: props.children }), void 0));

const AutoSubGrid = props => {
    const { minWidth = 0, columnSpan = 1, columnStart, columnEnd, rowStart, rowEnd, children } = props;
    const grid = useContext(AutoGridContext);
    if (columnStart && columnEnd) {
        throw new Error(`
      Cannot set both columnStart and columnEnd. 
      Set only one and use columnSpan.
    `);
    }
    const getColumns = useCallback((wanted) => {
        // the total width of the columns + the gaps to span the wanted length
        const totalWidth = (wanted * grid.columnWidth) + (grid.gridGap[1] * (wanted - 1));
        if (totalWidth < minWidth && wanted < grid.columnCount) {
            return getColumns(wanted + 1);
        }
        return wanted;
    }, [grid, minWidth]);
    const span = getColumns(Math.min(columnSpan, grid.columnCount));
    let start = columnStart, end = columnEnd;
    if (columnEnd && !columnStart) {
        start = `span ${span}`;
    }
    else if (!columnEnd) {
        end = `span ${span}`;
    }
    return (jsxRuntime.exports.jsx(Container.GridItem, Object.assign({ "$gridColumnStart": start, "$gridColumnEnd": end, "$gridRowStart": rowStart, "$gridRowEnd": rowEnd }, { children: jsxRuntime.exports.jsx(AutoGrid$1, Object.assign({ maxColumns: span, gridGap: grid.gridGap }, { children: children }), void 0) }), void 0));
};

const AutoGrid = {
    Grid: AutoGrid$1,
    Cell: AutoGridCell,
    FullRow,
    SubGrid: AutoSubGrid,
};

const StaticWrapper = props => (jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$zIndex": "999", "$position": "fixed", "$top": `calc(50% - (${props.size}/2))`, "$left": `calc(50% - (${props.size}/2))` }, { children: props.children }), void 0));
const OverlayWrapper = props => (jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$zIndex": "999", "$position": "absolute", "$top": "0", "$left": "0", "$right": "0", "$bottom": "0", "$backgroundColor": "white@.75" }, { children: jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$zIndex": "999", "$position": "absolute", "$top": `calc(50% - (${props.size}/2))`, "$left": `calc(50% - (${props.size}/2))` }, { children: props.children }), void 0) }), void 0));
const BasicWrapper = props => (jsxRuntime.exports.jsx("div", { children: props.children }, void 0));
const Loader = props => {
    const { color = "primary", size = '6rem', mode = 'basic' } = props;
    const Wrapper = mode === 'fixed' ? StaticWrapper : mode === 'overlay' ? OverlayWrapper : BasicWrapper;
    return (jsxRuntime.exports.jsx(Wrapper, Object.assign({ size: size }, { children: jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$width": "100%", "$height": "100%", "$alignItems": "center", "$justifyContent": "center" }, { children: jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$gridTemplateColumns": size, "$gridTemplateRows": size }, { children: jsxRuntime.exports.jsx(Box.Simple, Object.assign({ "$width": "100%", "$height": "100%", "$borderRadius": "100%", "$backgroundColor": `${color}@.30` }, { children: jsxRuntime.exports.jsx(Icon.Loader, { "$color": color }, void 0) }), void 0) }), void 0) }), void 0) }), void 0));
};

class ValidationError extends Error {
    constructor() {
        super(...arguments);
        this.kind = 'ValidaitonError';
    }
}
function isValidationError(e) {
    return (typeof e === 'object') && (e !== null) && ('kind' in e) && (e.kind === 'ValidaitonError');
}
const none = v => { };
const required = v => {
    if (!v) {
        throw new ValidationError('Required');
    }
};
const email = v => {
    const re = new RegExp(/^[A-Z0-9._%+-]+@[A-Z0-9-.]+\.[A-Z]{1,}$/i);
    if (v && !v.match(re)) {
        throw new ValidationError('Must be a valid email');
    }
};
const phone = v => {
    if (v && !v.match(/^[2-9]\d{9}$/)) {
        throw new ValidationError('Must be a valid phone number');
    }
};
/** Only allows whole numbers */
const integer = v => {
    if (v && v.match(/\D/)) {
        throw new ValidationError('Must be a whole number');
    }
};
const decimal = v => {
    if (v) {
        const parts = v.split('.');
        if (parts.length > 2) {
            throw new ValidationError('Too many decimal points');
        }
        parts.forEach(part => check(integer, part, 'Invalid Number'));
    }
};
const minLength = (min) => v => {
    if (v && v.length < min) {
        throw new ValidationError(`Minimum ${min} characters required`);
    }
};
const maxLength = (max) => v => {
    if (v && v.length > max) {
        throw new ValidationError(`Maximum ${max} characters only`);
    }
};
const lte = (max) => v => {
    if (v && Number(v) > max) {
        throw new ValidationError(`Cannot exceed ${max}`);
    }
};
const gte = (min) => v => {
    if (v && Number(v) < min) {
        throw new ValidationError(`Must be at least ${min}`);
    }
};
const maxDecimalPlace = (place) => v => {
    const max = Math.floor(place);
    if (v) {
        const parts = v.split('.');
        if (parts[0].length > max) {
            throw new ValidationError('Too many decimal places');
        }
        check(decimal, v);
    }
};
const oneOf = (valid) => v => {
    if (v && !valid.includes(v)) {
        throw new ValidationError(`Must be one of "${valid.join(', ')}"`);
    }
};
const matchesRegex = (valid) => v => {
    if (v && !v.match(valid)) {
        throw new ValidationError(`Invalid Input`);
    }
};
const yesOrNo = v => {
    if (v && !['yes', 'no'].includes(v.toLowerCase())) {
        throw new ValidationError(`Must be either Yes or No`);
    }
};
const password = [
    matchesRegex(/[a-z]/),
    matchesRegex(/[A-Z]/),
    matchesRegex(/[0-9]/),
    matchesRegex(/[-+_!@#$%^&*., ?]/),
    minLength(8),
    maxLength(50),
];
const currencyOf = (vs) => v => {
    const value = (v || '').replace(/[^\d.]/g, '');
    vs.forEach(validation => {
        check(validation, value);
    });
};
const ORIGINAL_ERROR = '%original-error%';
function check(validations, value, message) {
    if (typeof validations === 'function') {
        try {
            validations(value);
        }
        catch (e) {
            if (isValidationError(e)) {
                if (message) {
                    throw new ValidationError(message.replace(ORIGINAL_ERROR, e.message));
                }
                else {
                    throw e;
                }
            }
            else {
                throw e;
            }
        }
    }
    else {
        validations.forEach(inner => check(inner, value, message));
    }
}

var validations = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ValidationError: ValidationError,
    isValidationError: isValidationError,
    none: none,
    required: required,
    email: email,
    phone: phone,
    integer: integer,
    decimal: decimal,
    minLength: minLength,
    maxLength: maxLength,
    lte: lte,
    gte: gte,
    maxDecimalPlace: maxDecimalPlace,
    oneOf: oneOf,
    matchesRegex: matchesRegex,
    yesOrNo: yesOrNo,
    password: password,
    currencyOf: currencyOf,
    ORIGINAL_ERROR: ORIGINAL_ERROR,
    check: check
});

const FormContext = createContext({
    hasSubmitted: false,
    errors: {},
    checkValidation() { }
});
const useFormRevalidate = () => {
    const form = useContext(FormContext);
    const revalidate = useCallback(() => {
        if (form.hasSubmitted) {
            form.checkValidation();
        }
    }, [form]);
    return revalidate;
};
const useFormFieldError = (name) => {
    const form = useContext(FormContext);
    const error = form.errors[name];
    return {
        error: (error && error.length && error[0].message) || undefined,
        hasSubmitted: form.hasSubmitted,
    };
};
const useFormBaseError = () => {
    const form = useContext(FormContext);
    const error = form.baseError;
    return error;
};
const useFormErrorCount = () => {
    const form = useContext(FormContext);
    return Object.keys(form.errors).length;
};
const isInput = (e) => {
    return e.tagName.toLowerCase() === 'input';
};
const isTextArea = (e) => {
    return e.tagName.toLowerCase() === 'textarea';
};
const isSelect = (e) => {
    return e.tagName.toLowerCase() === 'select';
};
const isFormField = (e) => {
    return isInput(e) || isTextArea(e) || isSelect(e);
};
function isAxiosError(e) {
    return (e && typeof e === 'object' && ('isAxiosError' in e) && e.isAxiosError);
}
const useIsMounted = () => {
    const mounted = useRef(true);
    useEffect(() => {
        return () => {
            mounted.current = false;
        };
    }, []);
    return mounted.current;
};
function reduceError(e) {
    if (!e) {
        return {};
    }
    else if (Array.isArray(e)) {
        return { base: e[0] };
    }
    else if (typeof e === 'string') {
        return { base: e };
    }
    else {
        const results = {};
        Object.keys(e).forEach(key => {
            const value = e[key];
            const result = Array.isArray(value) ? value[0] : value;
            results[key] = result;
        });
        return results;
    }
}
const Form$1 = props => {
    const { minColumnWidth = 250, maxColumns = 3, gridGap = [0, 30], validations, onSubmit } = props;
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [submitting, setSubitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [baseError, setBaseError] = useState();
    const ref = useRef(null);
    const mounted = useIsMounted();
    const checkValidation = useCallback(() => {
        const fields = Array.from(ref.current.elements).filter(isFormField);
        const data = fields.reduce((data, field) => {
            if (!field.name || field.name in data) {
                return data;
            }
            return Object.assign(Object.assign({}, data), { [field.name]: field.value });
        }, {});
        const errors = (validations || []).reduce((errors, validation) => {
            const [name, rules, message] = validation;
            const element = fields.find(e => e.name === name);
            try {
                check(rules, element === null || element === void 0 ? void 0 : element.value, message);
            }
            catch (err) {
                if (isValidationError(err)) {
                    if (!errors[name]) {
                        errors[name] = [];
                        errors[name].push(err);
                    }
                }
            }
            return errors;
        }, {});
        setErrors(errors);
        const errorCount = Object.keys(errors).length;
        return [errorCount, data];
    }, [validations]);
    const handleUnknownError = useCallback(() => {
        setBaseError('An error occured, please try again or contact support.');
    }, []);
    const handleAxiosError = useCallback((ae) => {
        let errors = {};
        if (ae.response) {
            errors = reduceError(ae.response.data.error || ae.response.data.errors);
        }
        if (errors) {
            const fields = Array.from(ref.current.elements).filter(isFormField);
            const fieldNames = fields.map(field => field.name).filter(name => !!name && errors[name]);
            if (!fieldNames.length && !errors.base) {
                handleUnknownError();
            }
            if (errors.base) {
                setBaseError(errors.base);
            }
            if (fieldNames.length) {
                setErrors(fieldNames.reduce((memo, next) => {
                    const message = errors[next];
                    memo[next] = [new Error(message)];
                    return memo;
                }, {}));
            }
        }
        else {
            handleUnknownError();
        }
    }, [handleUnknownError]);
    const handleSubmit = useCallback((event) => __awaiter(void 0, void 0, void 0, function* () {
        event.preventDefault();
        setBaseError(undefined);
        const [errorCount, data] = checkValidation();
        if (errorCount) {
            setHasSubmitted(true);
        }
        else {
            if (onSubmit) {
                setSubitting(true);
                try {
                    yield onSubmit(data);
                }
                catch (e) {
                    if (isAxiosError(e)) {
                        handleAxiosError(e);
                    }
                    else {
                        handleUnknownError();
                    }
                }
                if (mounted) {
                    setSubitting(false);
                }
            }
        }
    }), [checkValidation, onSubmit, mounted, handleAxiosError, handleUnknownError]);
    return (jsxRuntime.exports.jsx(FormContext.Provider, Object.assign({ value: { hasSubmitted, errors, baseError, checkValidation } }, { children: jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$position": "relative" }, { children: jsxRuntime.exports.jsxs("form", Object.assign({ ref: ref, onSubmit: handleSubmit }, { children: [jsxRuntime.exports.jsx(AutoGrid.Grid, Object.assign({ minColumnWidth: minColumnWidth, maxColumns: maxColumns, gridGap: gridGap }, { children: props.children }), void 0), submitting ? jsxRuntime.exports.jsx(Loader, { mode: "overlay" }, void 0) : null] }), void 0) }), void 0) }), void 0));
};

const MAX_INT = 9999;
const defaultTemplate = [MAX_INT];
const defaultAlledChars = /./;
const textFormat = {};
const nameFormat = {
    allowedChars: /[-a-zA-Z. ']/,
};
const emailFormat = {
    allowedChars: /[^\s]/,
    template: [250]
};
const phoneFormat = {
    allowedChars: /\d/,
    preprocess: str => str.replace(/^1/, ''),
    template: ['(', 3, ') ', 3, '-', 4]
};
const integerFormat = {
    allowedChars: /\d/,
};
const applyFormat = (format, text) => {
    if (format.preprocess) {
        text = format.preprocess(text);
    }
    if (!text.length) {
        return { formatted: '', raw: '' };
    }
    let chars = text.match(new RegExp((format.allowedChars || defaultAlledChars).source, 'g')) || [];
    if (format.preformat) {
        chars = format.preformat(chars);
    }
    let formatted = '';
    let raw = '';
    const template = (format.template || defaultTemplate);
    template.forEach(f => {
        if (typeof f === 'string') {
            if (chars.length) {
                formatted += f;
            }
        }
        else {
            const next = chars.splice(0, f).join('');
            formatted += next;
            raw += next;
        }
    });
    return { formatted, raw };
};

const Input = makeComponentFor('input', 'form.input');
const FormFieldContext = createContext({
    raw: '',
    formatted: '',
    input: null,
});
const InputTypes = {
    text: {
        format: textFormat,
        type: 'text',
    },
    password: {
        format: textFormat,
        type: 'password',
    },
    name: {
        format: nameFormat,
        type: 'text',
    },
    email: {
        format: emailFormat,
        type: 'text',
    },
    phone: {
        format: phoneFormat,
        type: 'tel',
    },
    integer: {
        format: integerFormat,
        type: 'tel',
    }
};
const FieldLabel = props => {
    const { error, hasSubmitted } = useFormFieldError(props.name);
    const valid = hasSubmitted && !error;
    const label = valid ? jsxRuntime.exports.jsxs(jsxRuntime.exports.Fragment, { children: ["\u2713 ", props.children] }, void 0) : props.children;
    return (jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$position": "absolute", "$left": "0.5rem", "$top": "1rem" }, { children: jsxRuntime.exports.jsx("label", { children: jsxRuntime.exports.jsx(Text.Caption, Object.assign({ "$textAlign": "left", "$color": error ? 'error' : valid ? 'success' : undefined }, { children: error || label }), void 0) }, void 0) }), void 0));
};
function getSelectionInFormat(format, selection) {
    var _a;
    let [start, end] = selection;
    let cursor = 0;
    (_a = format.template) === null || _a === void 0 ? void 0 : _a.forEach(part => {
        if (typeof part === 'number') {
            cursor += part;
        }
        else {
            const extra = part.length;
            const spanning = end !== null && end > cursor && end <= cursor + extra;
            if (spanning) {
                start = start !== null ? start += extra : null;
                end = end !== null ? end += extra : null;
            }
            cursor += extra;
        }
    });
    return [start, end];
}
const Field = props => {
    const { name, type: inputType = "text", label, defaultValue, value: externalValue, disabled, columnSpan, minWidth, columnStart, columnEnd, onChange } = props;
    const { format, type } = InputTypes[inputType];
    const ref = useRef(null);
    const revalidate = useFormRevalidate();
    const { error, hasSubmitted } = useFormFieldError(name);
    const [validatedValue, setValidatedValue] = useState(null);
    const [internalValue, setInternalValue] = useState(defaultValue);
    const [selection, setSelection] = useState([null, null]);
    const handleChange = useCallback(event => {
        event.persist();
        if (ref.current) {
            setSelection([ref.current.selectionStart, ref.current.selectionEnd]);
        }
        if (onChange) {
            onChange(event);
        }
        setInternalValue(event.target.value);
    }, [onChange]);
    const value = externalValue || internalValue || '';
    useEffect(() => {
        // This `if` prevents an endless loop
        if (validatedValue !== value) {
            revalidate();
            setValidatedValue(value);
            if (ref.current) {
                let newSelection;
                if (validatedValue && value && value.length < validatedValue.length) {
                    // deleting
                    newSelection = selection;
                }
                else {
                    // adding (need to account for new formatting chars)
                    newSelection = getSelectionInFormat(format, selection);
                }
                // Reset cursor after formatting
                ref.current.setSelectionRange(newSelection[0], newSelection[1]);
            }
        }
    }, [format, revalidate, selection, validatedValue, value]);
    const { raw, formatted } = applyFormat(format, value);
    return (jsxRuntime.exports.jsx(AutoGridCell, Object.assign({ columnSpan: columnSpan, minWidth: minWidth, columnStart: columnStart, columnEnd: columnEnd }, { children: jsxRuntime.exports.jsxs(Container.Position, Object.assign({ "$position": "relative" }, { children: [jsxRuntime.exports.jsx(FieldLabel, Object.assign({ name: name }, { children: value ? label : "\u00a0" }), void 0), jsxRuntime.exports.jsx(Input, { ref: ref, onChange: handleChange, type: type, value: formatted, disabled: disabled, placeholder: label, "$borderStyle": "none", "$borderWidth": "1px", "$borderBottomStyle": "solid", "$borderBottomColor": error ? 'error' : hasSubmitted ? 'success' : 'grey.light', "$placeholderColor": error ? 'error.light' : 'grey', "$focusOutlineColor": error ? 'error' : hasSubmitted ? 'success' : 'primary' }, void 0), jsxRuntime.exports.jsx("input", { type: "hidden", name: name, value: raw }, void 0), jsxRuntime.exports.jsx(FormFieldContext.Provider, Object.assign({ value: { raw, formatted, input: ref.current } }, { children: props.children }), void 0)] }), void 0) }), void 0));
};

const RadioContext = createContext({ value: '', disabled: false, onChange: (value) => undefined });
const RadioButton = props => {
    const { checked } = props;
    return (jsxRuntime.exports.jsx(Box.Model, Object.assign({ "$borderColor": "grey.light", "$borderWidth": "1px", "$borderStyle": "solid", "$borderRadius": "100%", "$width": "3rem", "$height": "3rem" }, { children: jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$alignItems": "center", "$justifyContent": "center", "$width": "100%", "$height": "100%" }, { children: jsxRuntime.exports.jsx(Box.Model, { "$backgroundColor": checked ? 'primary' : 'clear', "$borderRadius": "100%", "$width": "1.6rem", "$height": "1.6rem" }, void 0) }), void 0) }), void 0));
};
const RadioField = props => {
    const { label, value, disabled: selfDisabled } = props;
    const group = useContext(RadioContext);
    const checked = value === group.value;
    const disabled = group.disabled || selfDisabled;
    const { onChange } = group;
    const activatedHandler = useCallback(() => {
        if (value && onChange && !disabled) {
            onChange(value);
        }
    }, [disabled, onChange, value]);
    const handleKeyPress = useCallback(event => {
        if (event.key === ' ') {
            activatedHandler();
        }
    }, [activatedHandler]);
    return (jsxRuntime.exports.jsxs(Container.Grid, Object.assign({ "$gridTemplateColumns": "3rem 1fr", "$gridGap": "1.5rem", "$alignItems": "center", "$focusOutlineColor": "primary", "$cursor": disabled ? 'default' : 'pointer', tabIndex: 0, onKeyPress: handleKeyPress, onClick: activatedHandler }, { children: [jsxRuntime.exports.jsx(RadioButton, { checked: checked, disabled: disabled }, void 0), jsxRuntime.exports.jsx(Text.Body1, Object.assign({ "$textAlign": "left" }, { children: label }), void 0)] }), void 0));
};
const RadioGroup = props => {
    const { name, children, disabled = false, defaultValue, value: externalValue, onChange } = props;
    const [internalValue, setInternalValue] = useState(defaultValue);
    const value = externalValue || internalValue || '';
    const handleChange = useCallback((newValue) => {
        setInternalValue(newValue === value ? '' : newValue);
        if (onChange) {
            onChange(name, newValue);
        }
    }, [name, value, onChange]);
    return (jsxRuntime.exports.jsxs(Container.Base, Object.assign({ "$display": "contents" }, { children: [jsxRuntime.exports.jsx(RadioContext.Provider, Object.assign({ value: { value, disabled, onChange: handleChange } }, { children: children }), void 0), jsxRuntime.exports.jsx("input", { type: "hidden", name: name, value: value }, void 0)] }), void 0));
};

const Select = makeComponentFor('select', 'form.select');
const SelectField = props => {
    const { name, label, defaultValue, value: externalValue, disabled, columnSpan, minWidth, columnStart, columnEnd, onChange } = props;
    const revalidate = useFormRevalidate();
    const { error, hasSubmitted } = useFormFieldError(name);
    const [validatedValue, setValidatedValue] = useState(null);
    const [internalValue, setInternalValue] = useState(defaultValue);
    const handleChange = useCallback(event => {
        event.persist();
        if (onChange) {
            onChange(event);
        }
        setInternalValue(event.target.value);
    }, [onChange]);
    const value = externalValue || internalValue || '';
    useEffect(() => {
        // This `if` prevents an endless loop
        if (validatedValue !== value) {
            revalidate();
            setValidatedValue(value);
        }
    }, [revalidate, validatedValue, value]);
    return (jsxRuntime.exports.jsx(AutoGridCell, Object.assign({ columnSpan: columnSpan, minWidth: minWidth, columnStart: columnStart, columnEnd: columnEnd }, { children: jsxRuntime.exports.jsxs(Container.Position, Object.assign({ "$position": "relative" }, { children: [jsxRuntime.exports.jsx(FieldLabel, Object.assign({ name: name }, { children: value ? label : "\u00a0" }), void 0), jsxRuntime.exports.jsxs(Select, Object.assign({ name: name, "$color": error ? 'error.light' : value ? undefined : 'grey', onChange: handleChange, value: value, disabled: disabled, "$borderStyle": "none", "$borderWidth": "1px", "$borderBottomStyle": "solid", "$borderBottomColor": error ? 'error' : hasSubmitted ? 'success' : 'grey.light', "$placeholderColor": error ? 'error.light' : 'grey', "$focusOutlineColor": error ? 'error' : hasSubmitted ? 'success' : 'primary' }, { children: [label && jsxRuntime.exports.jsx("option", Object.assign({ disabled: !!value, value: "" }, { children: label }), void 0), props.children] }), void 0), jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$position": "absolute", "$right": "0.5rem", "$bottom": "1rem", "$width": "1rem" }, { children: jsxRuntime.exports.jsx(Icon.TriangleDown, { "$color": error ? 'error' : hasSubmitted ? 'success' : 'primary' }, void 0) }), void 0)] }), void 0) }), void 0));
};

const Submit = props => {
    const { columnSpan = 1, minWidth } = props;
    const grid = useContext(AutoGridContext);
    const errorCount = useFormErrorCount();
    return (jsxRuntime.exports.jsxs(Container.Base, Object.assign({ "$display": "contents" }, { children: [Array.from({ length: grid.columnCount - columnSpan }).map((u, i) => jsxRuntime.exports.jsx("span", {}, i)), jsxRuntime.exports.jsx(AutoGridCell, Object.assign({ columnSpan: columnSpan, minWidth: minWidth, columnEnd: "end" }, { children: jsxRuntime.exports.jsx(Button.Primary, Object.assign({ "$width": "100%", disabled: !!errorCount }, { children: props.children }), void 0) }), void 0)] }), void 0));
};

const Form = {
    Container: Form$1,
    Input: Field,
    Radio: {
        Group: RadioGroup,
        Field: RadioField,
    },
    Select: SelectField,
    Submit,
    validations,
    InputContext: FormFieldContext,
};

const ButtonBarButton = ({ option, selected, onClick, bar: { hue, selectedHue = hue, selectedColor, selectedBackgroundColor } }) => (jsxRuntime.exports.jsx(Button.Primary, Object.assign({ onClick: onClick, "$borderRadius": "0", "$width": "100%", "$hue": selected ? selectedHue : hue, "$backgroundColor": selected ? selectedBackgroundColor : undefined, "$borderColor": selected ? selectedBackgroundColor : undefined, "$color": selected ? selectedColor : undefined }, { children: option.label }), void 0));
const ButtonBar = props => {
    const { buttons, onChange, containerColor, selectedValue, defaultSelectedValue, buttonComp } = props;
    const Button = buttonComp || ButtonBarButton;
    const [internalSelected, setInternalSelected] = useState(defaultSelectedValue);
    const selected = selectedValue || internalSelected;
    const handleClick = useCallback((opt) => () => {
        const { value } = opt;
        setInternalSelected(value);
        if (onChange) {
            onChange(value);
        }
    }, [onChange]);
    return (jsxRuntime.exports.jsx(Box.Model, Object.assign({ "$width": "max-content", "$color": containerColor, "$borderRadius": "buttons.borderRadius", "$borderWidth": "1px", "$borderStyle": "solid", "$backgroundColor": "currentColor", "$overflow": "hidden" }, { children: jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$gridTemplateColumns": `repeat(${buttons.length}, 1fr)`, "$width": "max-content", "$gridGap": "1px" }, { children: buttons.map(opt => (jsxRuntime.exports.jsx(Button, { option: opt, selected: opt.value === selected, onClick: handleClick(opt), bar: props }, opt.value))) }), void 0) }), void 0));
};

const Tooltip = props => {
    const [showing, setShowing] = useState(null);
    const show = useCallback((e) => setShowing(e.currentTarget), []);
    const hide = useCallback(() => setShowing(null), []);
    const handleRef = useCallback((el) => {
        if (el) {
            el.addEventListener('mouseenter', show);
            el.addEventListener('mouseleave', hide);
            return () => {
                el.removeEventListener('mouseenter', show);
                el.removeEventListener('mouseleave', hide);
            };
        }
    }, [show, hide]);
    return (jsxRuntime.exports.jsxs(Container.Base, Object.assign({ ref: handleRef, "$display": "contents" }, { children: [props.children, showing && props.content && jsxRuntime.exports.jsx(TooltipUI, { on: showing, content: props.content }, void 0)] }), void 0));
};
const TooltipUI = props => {
    const { content } = props;
    const parent = props.on.children[0].closest('.tooltip-container') || props.on.children[0].offsetParent || document.body;
    const rect = props.on.children[0].getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    const endX = Math.min(rect.x + rect.width, parentRect.x + parentRect.width);
    const startX = Math.max(rect.x, parentRect.x);
    const startY = Math.max(rect.top, parentRect.top);
    const width = endX - startX;
    const x = startX + (width / 2);
    const y = startY;
    const y2 = rect.bottom;
    const notchIconSize = `1rem`;
    const [offset, setOffset] = useState(0);
    const [inverted, setInverted] = useState(false);
    const ref = useRef(null);
    useLayoutEffect(() => {
        if (ref.current) {
            const div = ref.current;
            const b = div.getBoundingClientRect();
            const margin = 10;
            const half = margin + (b.width / 2);
            const top = y - margin - b.height;
            if (x < half) {
                setOffset(half - x);
            }
            else if (x > document.body.offsetWidth - half) {
                setOffset((document.body.offsetWidth - half) - x);
            }
            if (top < 0) {
                setInverted(true);
            }
            else if (inverted) {
                setInverted(false);
            }
        }
    }, [x, y, inverted]);
    return createPortal(jsxRuntime.exports.jsx(Container.Position, Object.assign({ ref: ref, "$transform": `translate(-50%, ${inverted ? `${notchIconSize}` : `calc(-100% - ${notchIconSize})`})`, "$position": "absolute", "$top": inverted ? `${y2}px` : `${y}px`, "$left": `${x + offset}px`, "$maxWidth": "20rem", "$minWidth": "5rem", "$width": "max-content" }, { children: jsxRuntime.exports.jsx(Filter.DropShadow, { children: jsxRuntime.exports.jsxs(Container.Position, Object.assign({ "$position": "relative", "$backgroundColor": "grey.darker", "$borderRadius": "5px", "$padding": "1rem" }, { children: [typeof content === 'string' ? (jsxRuntime.exports.jsx(Text.Caption, Object.assign({ "$textAlign": "center", "$color": "grey.lighter" }, { children: content }), void 0)) : (content), jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$display": "grid", "$position": "absolute", "$top": !inverted ? '100%' : undefined, "$bottom": inverted ? '100%' : undefined, "$transform": inverted ? 'rotate(180deg)' : undefined, "$left": `calc(50% - (${notchIconSize} / 2) - ${offset}px)`, "$height": notchIconSize, "$width": notchIconSize }, { children: jsxRuntime.exports.jsx(Icon.TooltipNotch, { "$color": "grey.darker" }, void 0) }), void 0)] }), void 0) }, void 0) }), void 0), document.getElementById('tooltip'));
};

export { AutoGrid, Box, Button, ButtonBar, Container, dark as DarkTheme, Effect, Filter, Form, Icon, Loader, theme as MainTheme, Spacer, Span, Text, Tooltip, useFormBaseError, useFormFieldError };
